/**
 * 進貨單建立表單(REQ-20260806002,入帳欄位由 REQ-20260813001 擴充)。
 *
 * 與出貨單的差別:
 *  - 對象是廠商,還多一個「進貨類別」(選項由設定頁的 `purchase-categories` 管理,
 *    REQ-20260814002;預設就是原本寫死的紙張採購 / 發外包加工)
 *  - 可指定**代管客戶**:確認時系統判定「進貨 > 出貨」,超出的部分會自動
 *    拋轉成該客戶的代管庫存,訪談的商品圖片一併帶過去;選了之後品項的
 *    「工單 / 案號」會列出該客戶近期的應收單號當建議(REQ-20260819002),
 *    清單與**已選的 chip** 都顯示「單號 - 品名 等N項」(REQ-20260819003、REQ-20260905001),
 *    但存進 DB 與列印的仍然只有純單號(`VARCHAR(12)`)。
 *    這一格自 REQ-20260905003 起是**複選**(挑選框而不是 `<datalist>`):
 *    一令紙可能同時給好幾張客戶訂單用,而為了記這件事把品項拆成兩列會讓
 *    採購單價變成假的(廠商的一令價 ≠ 兩次半令價)。**不做金額分攤**
 *  - 沒有才數 / 模數 / 版費 —— 那是報價給客戶的維度,進貨按數量計價
 *
 * 金額一律以 `calcPurchaseTotals()` 即時試算(與後端 Decimal 版逐案比對過),
 * 但**送出時前端算的金額不會被採信**,後端一定重算。
 *
 * 附件在建單後才能上傳:presign 的路徑需要 orderId。建立完成後表單會停在
 * 「附件」這一步,讓使用者把發票掃描檔傳完再離開。
 *
 * 表單最上面有單號區塊(REQ-20260813004),與出貨單表單同一套:建單時是**預覽值**
 * (跟後端要的,不是前端自己算),建完換成正式號碼。見 `OrderNoBlock`。
 *
 * 品項的欄位依**進貨類別**分流,四個判斷式互不影響
 * (REQ-20260818002、REQ-20260821005、REQ-20260821006、REQ-20260904003):
 *  - `showsProcessingFields()`(派工 / 外包 / 加工)→ 品項多出**印刷顏色**
 *    (「常用選項打勾 + 可手動輸入」的 `MultiSelectField`,複選)與**完成數量**。
 *    同一列的「加工 / 後製加工」(單選,REQ-20260821007:一項加工一列)**只留給
 *    非外包加工的類別**(例如純派工)—— 外包加工單的第一列已經有「加工項目」,
 *    兩格吃同一份 `processing-options`、操作也相同,並排等於重複(REQ-20260821008)
 *  - `isWorkOrderCategory()`(只認派工)→ 再多一組紙本派工單才有的生產指示:
 *    單頭的用紙廠商 / 交件日期 / 取件方式,品項的印工張數 / 成品尺寸 / 成品數量 /
 *    是否校色 / 特殊項目
 *  - `isOutsourceProcessingCategory()`(外包 / 加工,**排除派工**)→ 品項第一列換成
 *    加工版面(品名改標「加工項目」、吃印刷加工清單的單選挑選框,加工規格,
 *    單位移到數量後面),紙張維度那一列不顯示,「加工 / 後製加工」那一格也不渲染
 *    (欄位本身沒動,舊單的值照樣跟著送出)
 *  - `isGoodsPurchaseCategory()`(認「商品」,**排除派工與外包加工**)→ 品項換成
 *    商品採購版面(REQ-20260904003):第一組精簡成八格 —— 品名 / 規格描述 /
 *    材質描述 / 加工描述 / 數量 / 單位(挑選框,清單來自設定頁的貨品單位;
 *    版面是 `compact` 精簡版 —— 收合狀態下一行高,與左右兩格數量 / 單價對齊,
 *    REQ-20260905002)/ 單價 / 總價,
 *    而**「紙張規格」那一整組不渲染**(含庫存單位數量與換算率說明)。
 *    ⚠️ 關鍵字是「商品」不是「採購」—— 預設類別就叫「紙張採購」,用「採購」比對
 *    會把正式環境最大宗的紙張材料單一起換掉。
 *    ⚠️ 單位那格走 `setLine()` 而不是 `setLineUnitName()`:換算率整格藏起來了,
 *    選「令」還自動代 500 等於使用者看不到卻被偷偷乘 500。
 *
 * 紙張材料 / 配件五金這類純採購四個都不符合,維持精簡表單 —— 那幾格不顯示也不會被送出。
 *
 * 一般品項(紙張材料 / 派工類共用的那一支)的品項卡片自 REQ-20260905006 起依使用者
 * 附圖排成四組小標題 + 一組共用的成本歸屬(改動前是 REQ-20260903002 的三組):
 *  1. **品名**(單獨一列,8 欄)
 *  2. **紙張規格** —— 原紙規格 / 指定交付尺寸 / 每張刀裁(唯讀)、紙張顏色 / 厚度 / 紋路
 *  3. **數量與換算** —— 數量 / 換算率 / 庫存單位數量(唯讀)
 *  4. **金額** —— 單價 / 總價
 *  5. **成本歸屬**(兩種版面共用,外包加工類也看得到)
 *
 * 五組共用同一條 `md:grid-cols-12` 格線,每格用 `md:col-span-*` 指定寬度。
 * 加新欄位時**沿用這條 12 欄格線**,不要再開一條自己的 `md:grid-cols-N`
 * (REQ-20260903002 修的就是「三組各自 6 / 7 / 5 欄,直向對不齊」)。
 *
 * 「數量」與「厚度」是**數字框 + 貼在右邊的單位挑選鈕**(`NumberUnitField`,
 * REQ-20260905006):單位不再各自佔一格,收合狀態整格一行高。鈕點開的仍然是
 * `MultiSelectField`(精簡版),數量單位走 `setLineUnitName()`(選「令」自動代 500)、
 * 厚度單位走 `setLine()`。
 *
 * 原紙規格(`productSpec`)、指定交付尺寸(`sizeSpec`)、兩種單位都是**單選挑選框**
 * (建議不是值域,清單外的值照樣填得進去);尤其 `sizeSpec` **不能改回封閉的
 * `<select>`** —— 正式資料有清單外的舊值(「菊全」),會被靜默清掉,而它同時是
 * 派工單列印頁 `WorkOrderSheet` 的「紙張尺寸」。
 *
 * 「每張刀裁」是純顯示的一格(不進 `LineDraft` / payload / DB),值由指定交付尺寸推得,
 * 推法見 `cutsPerSheet()` —— 那是個**待使用者確認的假設**。
 *
 * 帶 `editOrder` 時進入編輯模式(REQ-20260814001,比照 `SalesOrderForm`)。
 * **只有草稿改得動**,那是後端既有的限制(R-08),不是這裡新加的 ——
 * 所以清單頁也只在草稿列上給「編輯」。編輯模式的差別只有三個:
 * 廠商不能換(`UpdatePurchaseOrderSchema` 沒有 `supplierId`)、單號直接顯示這張單的號、
 * 附件區塊就地展開(單已存在,presign 拿得到 orderId,不必再走一次建單後的附件步驟)。
 *
 * 帶 `copyFrom` 時是**以某張舊單為範本建新單**(REQ-20260911003,經常性採購省 key)。
 * 它**不是**編輯模式 —— `isEdit` 仍只看 `editOrder`,所以送出走 POST、單號走預覽配號、
 * 附件不複製。共用初始值的那條線叫 `seed`(= `editOrder ?? copyFrom`);
 * 刻意留白的是採購日期(改成今天)、到貨日期、發票號碼 / 發票日期、對帳單號、
 * 交件日期、來源訪談,以及品項的工單 / 案號(見 `toCopyLineDraft()`)。
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CreatePurchaseOrderSchema,
  FREIGHT_CASH_SOURCE_OPTIONS,
  FREIGHT_PAYMENT_MODES,
  PURCHASE_COST_ALLOCATION_MODES,
  PURCHASE_COST_DESTINATIONS,
  PURCHASE_GRAIN_DIRECTIONS,
  PURCHASE_SIZE_SPECS,
  PURCHASE_TAX_CATEGORIES,
  PURCHASE_LINE_PAYMENT_TYPES,
  PURCHASE_TAX_INCLUSIVE_TOLERANCE,
  PURCHASE_TAX_MODES,
  TRACKING_NO_SCAN_ACCEPT,
  UpdatePurchaseOrderSchema,
  calcLineAmount,
  FREIGHT_ALLOCATION_METHODS,
  allocateFreight,
  calcPurchaseTotals,
  calcStockUnitQty,
  purchaseTaxModeFromFields,
  purchaseTaxModeToFields,
  toNumber,
  toUnitPriceString,
  type CreatePurchaseOrderInput,
  type FreightAllocationMethod,
  type FreightPaymentMode,
  type PartyInfo,
  type PurchaseCostAllocationMode,
  type PurchaseCostDestination,
  type PurchaseGrainDirection,
  type PurchaseLinePaymentType,
  type PurchaseOrderDetail,
  type PurchasePaymentMethod,
  type PurchaseStockInType,
  type PurchaseTaxCategory,
  type PurchaseTaxMode,
  type TrackingNoCandidate,
} from '@app/shared';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import {
  MultiSelectField,
  joinItems,
  optionNames,
  splitItems,
} from '../../components/MultiSelectField';
import { PartyPicker } from '../../components/PartyPicker';
import { OpenRemindersBanner } from '../party-reminders/OpenRemindersBanner';
import { getApiErrorMessage } from '../../lib/api-client';
import { formatMoney } from '../../lib/format-money';
import { describeZodError, type FieldLabelMap } from '../../lib/zod-form-error';
import { referenceNameMap, useReferences } from '../settings/api';
import {
  isFreightCategory,
  isGoodsPurchaseCategory,
  isOutsourceProcessingCategory,
  isWorkOrderCategory,
  showsProcessingFields,
} from './work-order-category';
import {
  useCreatePurchaseOrder,
  useNextPurchaseOrderNo,
  usePartyById,
  useSalesOrders,
  useSubmitPurchaseOrder,
  useUpdatePurchaseOrder,
} from '../accounting/api';
import { DocumentAttachmentCard } from '../../components/DocumentAttachmentCard';
import { DocumentReceiptButton } from '../../components/DocumentReceiptButton';
import { uploadDocumentAttachment } from '../document-attachments/api';
import { scanTrackingNo } from '../document-scan/api';

/**
 * 託運單號掃描的逐列狀態(REQ-20260907003)。
 *
 * 刻意**不放進 `LineDraft`**:這幾個值一個都不會送出,也不參與 dirty 判定 ——
 * 混進 draft 只會讓「表單有沒有被改過」多出一個假的來源。
 */
interface TrackingScanState {
  scanning: boolean;
  /** 多筆候選時列在欄位下方讓使用者點;只有一筆時直接帶入,所以這裡是空的 */
  candidates: TrackingNoCandidate[];
  /** 紅字提示(認不出來 / AI 沒設定 / 模型不支援讀圖 / 網路失敗) */
  error: string | null;
  /** 剛剛由圖片帶入 → 顯示「已由圖片帶入,請核對」 */
  filled: boolean;
}

const IDLE_SCAN: TrackingScanState = {
  scanning: false,
  candidates: [],
  error: null,
  filled: false,
};

interface LineDraft {
  productName: string;
  productSpec: string;
  /** 材質描述(REQ-20260904003)。只有商品採購版面有入口,其餘版面原值 pass-through */
  productMaterial: string;
  unitName: string;
  qty: string;
  unitPrice: string;
  /**
   * 品項總價。**只存在於畫面上**(REQ-20260821005)—— 送出的仍然只有數量與單價,
   * 後端一律拿 `qty × unitPrice` 重算金額。填總價時反推單價寫回 `unitPrice`。
   */
  amount: string;
  /** 紙張本身的顏色(REQ-20260902003)。與下面派工用的 `printColor` 是兩個欄位 */
  paperColor: string;
  weightGsm: string;
  /** 厚度 / 磅數的單位(REQ-20260902003):gsm/g、條、P、OZ、um/μm… */
  weightUnit: string;
  sizeSpec: string;
  grainDirection: '' | PurchaseGrainDirection;
  conversionRate: string;
  taxCategory: '' | PurchaseTaxCategory;
  /**
   * 工單 / 案號,**可複選**(REQ-20260905003)。存的是純單號,顯示才補品名。
   * 一令紙可能同時給好幾張客戶訂單用,而拆成兩列會讓採購單價變成假的。
   */
  workOrderNos: string[];
  costDestination: PurchaseCostDestination;
  // 派工單的生產指示(REQ-20260818002);非派工類的單這幾格不顯示、送出時是空的
  printColor: string;
  specialItems: string;
  colorProofRequired: boolean;
  impressionQty: string;
  finishedSize: string;
  finishedQty: string;
  postProcessing: string;
  /**
   * 廠商實際交回 / 完成的數量(REQ-20260821006)。委外加工會耗損 ——
   * 送廠 1100 片、交回 1060 片,這一格記的是後者。空字串 = 尚未回報(送出時是 null)。
   * 耗損量只在畫面上顯示,**不存**:它一律是 `qty − completedQty` 算得出來的。
   */
  completedQty: string;
  // ── 運輸費用單的明細欄位(REQ-20260907002)。只有運費版面填得到,其餘版面原值 pass-through
  /**
   * 這一列在 DB 的 id。**只在編輯既有單時有值** —— 明細列的「憑證」附件掛的是
   * `docType='PURCHASE_ORDER_LINE'` + `docId=lineId`,沒有 id 就沒有地方掛,
   * 所以建單模式下那顆鈕是停用的(與附件卡片同一種態度,不為它改建單流程)。
   * ⚠️ **不進 payload**:改單走的是「整批 deleteMany 後重建」,送回去也沒有意義。
   */
  id: number | null;
  shipDate: string;
  trackingNo: string;
  /** 送達客戶。存的是 id,顯示要簡稱,所以整個 PartyInfo 留在 draft 裡 */
  deliveryCustomer: PartyInfo | null;
  /** 單頭選「逐筆指定」時才逐列可改;其餘三種送出時一律覆寫成單頭的值 */
  linePaymentType: '' | PurchaseLinePaymentType;
  // ── 運費歸單的來源與分攤(REQ-20260908002)──────────────────────────
  /** 這一列是從哪一筆運費明細歸過來的。手動補登的列是 null(畫面顯示「手動補登」) */
  freightLineId: number | null;
  /** 來源出貨所屬**訂單**的 id / 單號(⚠️ shipments 沒有自己的單號) */
  freightSourceOrderId: number | null;
  freightSourceOrderNo: string | null;
  /** 分攤方式。'' = 還沒分攤過(只有一張工單 / 尚未處理) */
  allocationMethod: '' | FreightAllocationMethod;
  /** 逐張工單分到多少(單號 → 金額字串)。手動指定時使用者打進來的也存這裡 */
  workOrderAmounts: Record<string, string>;
}

/**
 * 數量單位(REQ-20260824001 時叫「採購單位」)的常用選項。**只是建議不是值域** ——
 * 清單外的單位照樣從挑選框下面的手動輸入框打得進去,與印刷顏色 / 工單 案號那幾格
 * 同一種態度。
 *
 * 刻意寫死成常數而不是做成設定頁的 reference table:需求指名就是這幾個,
 * 而且手打本來就通,多一張可維護清單不會讓任何人少打一個字
 * (CLAUDE.md §2、REQ-20260824001 §2.2)。
 *
 * REQ-20260902003 補上「箱」(需求指名的五個是張 / 令 / 包 / 束 / 箱);
 * 「才」是 REQ-20260824001 使用者要過的,**保留不刪**。
 * 剛好 6 個 = `MultiSelectField` 的收合門檻,不必展開就全部看得到。
 */
const UNIT_NAME_OPTIONS = ['張', '令', '包', '束', '箱', '才'];

/**
 * 厚度 / 磅數的單位選項(REQ-20260902003)。作法與註解風格比照 `UNIT_NAME_OPTIONS`:
 * 寫死成常數、**不做成 reference table**(理由同上)。
 *
 * 不同紙材的厚度標示法不同 —— 只留一個數字欄位,看到「120」分不出是 120gsm
 * 還是 120 條。**存進 DB 的就是使用者選到 / 打到的那個字串,元件不做任何轉換**:
 * 括號裡的中文是給人看的說明,不是要被拆掉的。
 */
const WEIGHT_UNIT_OPTIONS = ['gsm/g(克重)', '條', 'P(磅)', 'OZ(盎司)', 'um/μm(微米)'];

/**
 * 大紙規格的常用選項(REQ-20260902003)。同樣是**建議不是值域** ——
 * 舊單存的規格(「菊全」這類)原樣顯示得出來,清單外的規格照樣打得進去。
 *
 * 與「尺寸 / 開數」的分工:這一格是**訂購的大張原紙**,那一格是**裁修完成交付的開數**。
 */
const PAPER_SPEC_OPTIONS = ['四六版', '菊版'];

/** 1 令 = 500 張 —— 選到「令」時自動代入的換算率(REQ-20260824001) */
const REAM_CONVERSION_RATE = '500';

/**
 * 「每張刀裁」(REQ-20260905006 圖二新增的一格)。**純顯示** ——
 * 不進 `LineDraft`、不進 payload、不進 DB,也不參與金額 / 庫存的任何計算。
 *
 * ⚠️ 這個推法是**假設**:取「指定交付尺寸」開頭的數字(`8K` → 8、`12K(信封)` → 12、
 * `G4K` → 4)。正式資料有清單外的舊值(「菊全」「四六全」),推不出來就顯示「—」,
 * 與圖二一致。使用者確認實際算法之前,不要拿它去算任何會落地的東西
 * (見 REQ-20260905006 的待確認事項)。
 */
function cutsPerSheet(sizeSpec: string): string {
  return /\d+/.exec(sizeSpec)?.[0] ?? '—';
}

/**
 * 唯讀「自動」欄位的千分位(REQ-20260905006)。**只用在 `<output>`** ——
 * 使用者正在打字的輸入框不能即時插逗號:受控 input 的推導值會吃掉按鍵、游標跳位。
 */
function withThousands(value: string): string {
  const [int = '', frac] = value.split('.');
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return frac == null ? grouped : `${grouped}.${frac}`;
}

/**
 * 品項那一排的**順向**跳格(REQ-20260911001)。四條規則逐條沿用
 * [REQ-20260904002] 在 `CreateReceivableModal` 立下的那一份,不重新論證:
 *
 * 1. 組字中(注音選字)的鍵是選字不是跳格 → 直接 return。
 * 2. **要攔哪些鍵由呼叫端給** —— 挑選框內部的 ↑ / ↓ 必須原封不動留給元件自己。
 * 3. `ArrowRight` 只有游標停在字尾、且沒有選取範圍時才跳格;游標在字中間時
 *    維持原生右移,否則打錯的字就移不回去改了。
 * 4. `preventDefault()` 之後用 `requestAnimationFrame` 延一幀才 focus ——
 *    讓瀏覽器 / 挑選框先把它那一份做完,不吃掉使用者剛選到的值。
 *
 * ⚠️ 與應收單那一份唯一的差別是**第三個參數**:進貨單的品項是可增列 / 刪列的
 * 多列表格,ref 以列索引為 key 存在 `Record<number, …>` 裡(比照檔內既有的
 * `scanInputs`),所以傳的是「去把那一格拿出來」的 getter 而不是單一 `RefObject`。
 * 這兩支**沒有匯出**,也刻意沒有跟應收單那兩支抽成共用模組 —— 那是另一件事。
 */
function focusNextOnKey(
  e: React.KeyboardEvent<HTMLInputElement>,
  keys: string[],
  next: () => HTMLInputElement | null | undefined,
): void {
  if (e.nativeEvent.isComposing) return;
  if (!keys.includes(e.key)) return;
  const el = e.currentTarget;
  if (
    e.key === 'ArrowRight' &&
    (el.selectionStart !== el.selectionEnd || el.selectionStart !== el.value.length)
  ) {
    return;
  }
  e.preventDefault();
  requestAnimationFrame(() => next()?.focus());
}

/**
 * `focusNextOnKey()` 的鏡像:同一排的**反向**退格(REQ-20260911001,規則沿用
 * [REQ-20260910001])。只有邊界條件相反 —— `ArrowLeft` 只有游標停在**最左邊**、
 * 且沒有選取範圍時才跳格。其餘三條與順向那支相同,不再重述。
 */
function focusPrevOnKey(
  e: React.KeyboardEvent<HTMLInputElement>,
  keys: string[],
  prev: () => HTMLInputElement | null | undefined,
): void {
  if (e.nativeEvent.isComposing) return;
  if (!keys.includes(e.key)) return;
  const el = e.currentTarget;
  if (e.key === 'ArrowLeft' && (el.selectionStart !== el.selectionEnd || el.selectionStart !== 0)) {
    return;
  }
  e.preventDefault();
  requestAnimationFrame(() => prev()?.focus());
}

/**
 * 整張表單的 Enter 守門(REQ-20260911001 需求第 1 點)。
 *
 * HTML 的 **implicit submission**:單行輸入框裡按 Enter 會送出整張表單 ——
 * 在這張表單上就是「KEY 到一半按 Enter 直接建單並跳出畫面」。打單過程中的 Enter
 * 應該是「填下一格」,所以掛在 `<form>` 上一律擋掉,存檔只剩底下那幾顆按鈕。
 *
 * 排除的目標:`<textarea>`(備註那格的 Enter 是換行)、按鈕、以及 type 為
 * submit / button / reset 的 input —— 那些的 Enter 是「按下這顆」,不是隱含送出。
 */
function blockImplicitSubmit(e: React.KeyboardEvent<HTMLFormElement>): void {
  if (e.key !== 'Enter' || e.nativeEvent.isComposing) return;
  const target = e.target;
  if (!(target instanceof HTMLInputElement)) return;
  if (target.type === 'submit' || target.type === 'button' || target.type === 'reset') return;
  e.preventDefault();
}

const EMPTY_LINE: LineDraft = {
  productName: '',
  productSpec: '',
  productMaterial: '',
  unitName: '',
  qty: '1',
  unitPrice: '0',
  amount: '0.00',
  paperColor: '',
  weightGsm: '',
  weightUnit: '',
  sizeSpec: '',
  grainDirection: '',
  conversionRate: '1',
  taxCategory: '',
  // `{ ...EMPTY_LINE }` 是淺拷貝,這個空陣列的參照會被所有新品項共用 ——
  // 安全的前提是**沒有人 mutate 它**:挑選框的 onChange 一律回傳新陣列,
  // `setLine()` 也是整個換掉。要加 push / splice 之前先改成每次現做一個。
  workOrderNos: [],
  costDestination: 'TO_STOCK',
  printColor: '',
  specialItems: '',
  colorProofRequired: false,
  impressionQty: '',
  finishedSize: '',
  finishedQty: '',
  postProcessing: '',
  completedQty: '',
  // 運輸費用單的明細欄位(REQ-20260907002)。其餘類別一律留空、也不會被送出
  id: null,
  shipDate: '',
  trackingNo: '',
  deliveryCustomer: null,
  linePaymentType: '',
  // 運費歸單的來源與分攤(REQ-20260908002)。手動新增的列一律沒有來源
  freightLineId: null,
  freightSourceOrderId: null,
  freightSourceOrderNo: null,
  allocationMethod: '',
  workOrderAmounts: {},
};

/**
 * 送出前的 zod 驗證失敗時,拿來把欄位路徑翻成畫面上的中文標籤(REQ-20260818003)。
 * 值就是各欄位 JSX 上的 label 文字 —— 使用者是照著畫面找那一格的。
 */
const FIELD_LABELS: FieldLabelMap = {
  supplierId: '廠商',
  orderType: '進貨類別',
  txnDate: '採購日期',
  receivedDate: '到貨日期',
  customerId: '代管客戶',
  paperSupplierId: '用紙廠商',
  dueDate: '交件日期',
  pickupMethod: '取件方式',
  notes: '備註',
  lines: '品項',
  'lines.#.productName': '品名',
  // 一般品項版面自 REQ-20260905006 起標「原紙規格」;商品採購版面標「規格描述」、
  // 外包加工版面標「加工規格」—— 三個入口同一個欄位,錯誤訊息沿用主要版面那一個
  // (同下面 `productMaterial` 的取捨)
  'lines.#.productSpec': '原紙規格',
  // 商品採購版面把這一格標成「材質描述」,但驗證訊息的文案是所有類別共用的 ——
  // 與 `productSpec`(商品採購版面標「規格描述」)同一種取捨,比照 REQ-20260821006
  // 對 `productName` 的處理:畫面標籤各自不同,錯誤訊息用共用的那一個
  'lines.#.productMaterial': '材質描述',
  'lines.#.unitName': '數量單位',
  'lines.#.qty': '數量',
  'lines.#.unitPrice': '單價',
  'lines.#.paperColor': '紙張顏色',
  // REQ-20260905006:單位併進輸入框後綴之後,這兩格畫面上就叫「厚度」與「指定交付尺寸」
  'lines.#.weightGsm': '厚度',
  'lines.#.weightUnit': '厚度單位',
  'lines.#.sizeSpec': '指定交付尺寸',
  'lines.#.grainDirection': '紋路',
  'lines.#.conversionRate': '換算率',
  'lines.#.taxCategory': '稅別',
  'lines.#.costDestination': '成本去向',
  'lines.#.workOrderNos': '工單 / 案號',
  'lines.#.printColor': '印刷顏色',
  'lines.#.impressionQty': '印工張數',
  'lines.#.finishedSize': '成品尺寸',
  'lines.#.finishedQty': '成品數量',
  'lines.#.colorProofRequired': '是否校色',
  'lines.#.postProcessing': '加工 / 後製加工',
  'lines.#.specialItems': '特殊項目',
  'lines.#.completedQty': '完成數量',
  // 運輸費用單的明細欄位(REQ-20260907002)
  'lines.#.shipDate': '運送日',
  'lines.#.trackingNo': '託運單號',
  'lines.#.deliveryCustomerId': '送達客戶',
  'lines.#.linePaymentType': '付款',
  statementNo: '對帳單號',
  freightPaymentMode: '付款方式',
  cashSource: '現付來源',
  payerName: '付款人',
  costAllocationMode: '成本歸屬',
};

/**
 * 合計旁那顆「稅別」下拉的值域(REQ-20260902011)。
 *
 * 平常就是廠商主檔的三個 token(含稅 / 稅外加 / 免稅)—— 使用者要求兩邊一致。
 * 多出來的 `'ZERO_RATED'` **只會出現在編輯舊單時**:零稅率在廠商主檔沒有對應值,
 * 但單據上存得下,舊單開起來必須還是零稅率(見 `legacyZeroRated`)。
 */
type TaxSelection = PurchaseTaxMode | 'ZERO_RATED';

/**
 * 這一列**生效**的付款方式(REQ-20260907002)。
 *
 * 單頭選「逐筆指定」(`PER_LINE`)時各列自己決定(沒挑過的落在月結);
 * 其餘三種時所有列一律跟著單頭走 —— 而且**送出時存的就是這個生效值**,
 * 不是「跟單頭」這種指標。理由:存指標的話,單頭事後被改掉會回頭改變
 * 舊列的金額歸屬,而「這趟到底誰付的」是已經發生過的事實。
 *
 * 單頭還沒選(空字串)時回 `null` —— 與非運費類的單完全相同,
 * 也就是 `calcPurchaseTotals()` 不排除任何一列。
 */
function effectiveLinePayment(
  line: Pick<LineDraft, 'linePaymentType'>,
  mode: '' | FreightPaymentMode,
): PurchaseLinePaymentType | null {
  if (mode === '') return null;
  if (mode === 'PER_LINE') return line.linePaymentType || 'MONTHLY';
  return mode;
}

/**
 * `YYYY-MM-DD` → `MM/DD`。**直接切字串**,不經過 `new Date()` ——
 * 這是一個「日期」不是「時間點」,轉成 Date 再格式化只會惹上時區
 * (CLAUDE.md §6.1)。沒填就顯示「—」。
 */
function shortMd(dateOnly: string): string {
  const m = /^\d{4}-(\d{2})-(\d{2})$/.exec(dateOnly);
  return m ? `${m[1]}/${m[2]}` : '—';
}

interface Props {
  onCreated: (orderNo: string) => void;
  onCancel: () => void;
  /** 有值即編輯模式;沒有就是建新單。後端只放行草稿(REQ-20260814001) */
  editOrder?: PurchaseOrderDetail;
  /**
   * 以這張單當範本建**新**單(REQ-20260911003)。經常性採購每次要打的內容幾乎一樣,
   * 從清單上指名一張舊單複製,比重打一次快。
   *
   * ⚠️ **不是編輯模式**:`isEdit` 仍然只看 `editOrder`,所以送出走 POST、單號走預覽配號、
   * 附件不複製。與 `editOrder` 互斥,清單頁不會同時傳。
   * 哪些欄位刻意不帶入見下方各處註解與 `toCopyLineDraft()`。
   */
  copyFrom?: PurchaseOrderDetail;
}

export function PurchaseOrderForm({ onCreated, onCancel, editOrder, copyFrom }: Props): JSX.Element {
  const isEdit = editOrder != null;
  /**
   * 初始值的來源(REQ-20260911003)。編輯是「這張單原本的值」、複製是「範本的值」,
   * 兩者對**大多數**欄位要做的事一樣,所以合成一個 `seed`。
   * 只在複製時該留白的那幾格(日期 / 發票 / 對帳單號 / 交件日期 / 來源訪談)
   * **維持讀 `editOrder`** —— 那才是「只有編輯模式帶回原值」的寫法。
   */
  const seed = editOrder ?? copyFrom;
  const [supplier, setSupplier] = useState<PartyInfo | null>(
    seed ? supplierFromOrder(seed) : null,
  );
  const [customer, setCustomer] = useState<PartyInfo | null>(
    seed ? customerFromOrder(seed) : null,
  );
  // 用紙廠商(REQ-20260818002)—— 派工單上「代工單位」與「用紙廠商」是兩家不同廠商。
  // 只是記錄用,應付仍然全數掛在代工單位(supplier)名下
  const [paperSupplier, setPaperSupplier] = useState<PartyInfo | null>(
    seed ? paperSupplierFromOrder(seed) : null,
  );
  const [pickupMethod, setPickupMethod] = useState(seed?.pickupMethod ?? '');
  // 進貨類別的清單來自設定頁(REQ-20260814002),不再寫死兩個 —— 空字串代表
  // 「還沒選」,建單時**刻意留成空的**要使用者自己選(REQ-20260907001,理由見下面)
  //
  // ⚠️ 複製模式**要**從範本帶入,這不違背 REQ-20260907001:那次要消掉的是
  // 「系統自己猜一個預設值」(使用者沒選過,畫面卻看起來已經選好了);
  // 複製是使用者指名要沿用某一張單,類別是他挑那張單時就看到的東西,不是猜的。
  // 何況類別決定品項要填哪一組規格欄位,不帶入的話品項版面會跟複製來的值對不上。
  const [orderType, setOrderType] = useState(seed?.orderType ?? '');
  // 複製模式刻意**不**帶範本的日期:今天開的單就是今天的單,單號的年月日也取它
  const [txnDate, setTxnDate] = useState(editOrder?.txnDate ?? todayInTaipei());
  // 到貨日期(REQ-20260916001)。與採購日期(`txnDate`)各自獨立 —— 它只記「東西
  // 哪天真的到」,不影響單號、應付月份與預計付款日。**選填**:下訂當下多半還不知道。
  // 複製模式讀不到這裡(留空):上一張單的到貨日跟這張新單無關
  const [receivedDate, setReceivedDate] = useState(editOrder?.receivedDate ?? '');
  // 交件日期(= 既有的 dueDate)。輸入格只在派工類顯示 —— 但**值一律帶回原值**,
  // 非派工類的單編輯時才不會把已經填好的到期日默默清掉(REQ-20260814001 §二.2)
  // 複製模式讀不到這裡(留空):交件日期是上一個案子談定的,照抄一定是錯的
  const [dueDate, setDueDate] = useState(editOrder?.dueDate ?? '');
  // 「發票與稅別」單頭欄位的 UI 已移除(REQ-20260813002),值固定用預設值送出。
  // ⚠️ 編輯模式要**帶回單據原值**而不是預設值:PATCH 是整張覆寫,送預設值等於把
  // 舊單(或 UI 移除前建的單)的發票號碼、稅別、入庫方式默默改掉,連總額都會變。
  //
  // ⚠️ 複製模式**一律留空**(不讀 `seed`):同一個廠商的同一張發票只能入一次帳(R-04),
  // 照抄過來送出必被後端擋下 —— 而且帶著別張單的發票號碼比留白更危險。
  const [invoiceNo] = useState(editOrder?.invoiceNo ?? '');
  const [invoiceDate] = useState(editOrder?.invoiceDate ?? '');
  // 例外:單頭稅別的入口在合計旁(REQ-20260901010),值域自 REQ-20260902011 起
  // 改成與廠商主檔一致的 含稅 / 稅外加 / 免稅。它同時是右下角三個數字的稅別依據,
  // 也是品項「留空 = 沿用單頭」沿用的對象。
  //
  // ⚠️ 舊單存的是零稅率時 `purchaseTaxModeFromFields()` 回 null —— 那個值在廠商主檔
  // 沒有對應,不能拿任何預設值蓋過去。這裡改成選中額外那顆「零稅率」選項
  // (見 `legacyZeroRated` 與下面的下拉),使用者不動它就原值送回。
  const editTaxMode = seed
    ? purchaseTaxModeFromFields(
        seed.taxCategory as PurchaseTaxCategory,
        seed.unitPriceTaxInclusive,
      )
    : null;
  const legacyZeroRated = seed != null && editTaxMode == null;
  const [taxMode, setTaxMode] = useState<TaxSelection>(
    editTaxMode ?? (seed ? 'ZERO_RATED' : 'EXCLUSIVE'),
  );
  // 「外加 5% / 內含」的入口在 REQ-20260824001 恢復過、REQ-20260901010 又移除,理由是
  // 「畫面上沒有入口卻會影響金額」。REQ-20260902011 起那個理由**不再成立** ——
  // 上面那顆下拉同時決定 `taxCategory` 與 `unitPriceTaxInclusive`,選「含稅」就是
  // 「我打的單價已經含稅」,使用者看得到也改得動。
  // 零稅率的舊單例外:那個選項對不到稅別,`unitPriceTaxInclusive` 一律**沿用單據原值**,
  // 不推導成 false(PATCH 是整張覆寫,推導等於把舊單的「內含」默默關掉、連總額都變)。
  const { taxCategory, unitPriceTaxInclusive } =
    taxMode === 'ZERO_RATED'
      ? {
          taxCategory: 'ZERO_RATED' as PurchaseTaxCategory,
          unitPriceTaxInclusive: seed?.unitPriceTaxInclusive ?? false,
        }
      : purchaseTaxModeToFields(taxMode);
  // 「入庫與付款」單頭欄位的 UI 亦已移除(REQ-20260813003),同樣固定用預設值送出。
  // 複製模式沿用範本的值 —— 同一家廠商的經常性採購,入庫方式與付款方式通常不變。
  const [stockInType] = useState<PurchaseStockInType>(
    (seed?.stockInType as PurchaseStockInType) ?? 'OWNED',
  );
  const [paymentMethod] = useState<'' | PurchasePaymentMethod>(
    (seed?.paymentMethod as PurchasePaymentMethod | null) ?? '',
  );
  // 預計付款日刻意**不**帶回原值:REQ-20260813003 決定它一律由後端依廠商付款條件推算。
  // 帶原值回去,改了採購日期就會被判定成「覆寫推算結果」而要求填覆寫原因(400)。
  const [expectedPaymentDate] = useState('');
  const [expectedPaymentDateReason] = useState('');
  const [notes, setNotes] = useState(seed?.notes ?? '');
  // ── 運輸費用單的單頭欄位(REQ-20260907002)。只有運費類渲染得到入口,
  //    其餘類別一律保持初始值 → 送出時是 null,與改動前逐字相同。
  //    ⚠️ 編輯模式一律**帶回單據原值**:PATCH 是整張覆寫,送初始值等於把舊單
  //    的對帳單號 / 付款方式默默清掉(同上面 invoiceNo 那組的理由)。
  //    ⚠️ 複製模式的對帳單號**一律留空**:對帳單號屬於那一期的對帳單,不能跨期沿用。
  const [statementNo, setStatementNo] = useState(editOrder?.statementNo ?? '');
  const [freightPaymentMode, setFreightPaymentMode] = useState<'' | FreightPaymentMode>(
    (editOrder?.freightPaymentMode as FreightPaymentMode | null) ?? '',
  );
  const [cashSource, setCashSource] = useState(editOrder?.cashSource ?? '');
  const [payerName, setPayerName] = useState(editOrder?.payerName ?? '');
  const [costAllocationMode, setCostAllocationMode] = useState<'' | PurchaseCostAllocationMode>(
    (editOrder?.costAllocationMode as PurchaseCostAllocationMode | null) ?? '',
  );
  const [lines, setLines] = useState<LineDraft[]>(
    seed ? seed.lines.map(editOrder ? toLineDraft : toCopyLineDraft) : [{ ...EMPTY_LINE }],
  );
  const [formError, setFormError] = useState<string | null>(null);
  /**
   * 「有品項未填單位」的提醒視窗(REQ-20260911001)。開著時記住當初按的是哪一顆 ——
   * 運費版的「確認入帳」走 `onSubmit(true)`,按「仍要儲存」得把那個 true 還原回去,
   * 否則存完就停在草稿、不會接著送出確認。
   */
  const [unitConfirm, setUnitConfirm] = useState<{ thenSubmit: boolean } | null>(null);

  // 建單完成後才進入的附件階段 —— presign 需要 orderId。
  // `failedUploads`:建單前先選好、但建單後補傳失敗的檔名(REQ-20261012001)
  const [createdOrder, setCreatedOrder] = useState<{
    id: number;
    orderNo: string;
    failedUploads: string[];
  } | null>(null);

  /**
   * 建單模式先選好的附件(REQ-20261012001),兩張卡片各一份。presign 需要單據 id,
   * 所以選檔當下不上傳,建單成功後才由 `uploadPendingFiles()` 逐一補傳。
   * 編輯模式不傳給卡片 —— 單已經存在,照舊選了就傳。
   */
  const [pendingDocFiles, setPendingDocFiles] = useState<File[]>([]);
  const [pendingWorkOrderFiles, setPendingWorkOrderFiles] = useState<File[]>([]);
  /** 補傳期間按鈕維持停用 —— 這時 createMutation 已經不是 pending 了,不攔會被重複送出 */
  const [uploadingPending, setUploadingPending] = useState(false);

  const createMutation = useCreatePurchaseOrder();
  const updateMutation = useUpdatePurchaseOrder();
  /**
   * 運費表單底部的「確認入帳」(REQ-20260907002)= **先存檔,再送出確認**。
   *
   * ⚠️ 它**沒有**跳過 R-08 的狀態機:單走到 `PENDING_CONFIRM`,最後那一步
   * 「確認」仍然只有 ADMIN 能在清單頁按(REQ-20260813001 §二)。
   * 按鈕文字照附圖叫「確認入帳」,底下那行小字說明它實際走到哪一步 ——
   * 自己在表單裡再開一條 confirm 路徑等於把清單頁的守門複製一份。
   */
  const submitMutation = useSubmitPurchaseOrder();
  const isPending =
    createMutation.isPending ||
    updateMutation.isPending ||
    submitMutation.isPending ||
    uploadingPending;

  /**
   * 單號預覽(REQ-20260813004)。採購日期一改就重取 —— 單號的年月日看的是
   * **單據日期**不是今天(REQ-20260809005)。詳細取捨見 `useNextPurchaseOrderNo()`。
   * 編輯模式不打這支:那張單的號已經定了。
   */
  const { data: previewOrderNo } = useNextPurchaseOrderNo(isEdit ? undefined : txnDate);

  /**
   * 編輯既有單據時,把代管客戶換成主檔的真資料(REQ-20260819004)。
   *
   * 單據只存了 `customerId` + 簡稱,`customerFromOrder()` 湊出來的 `PartyInfo` 沒有編號
   * 也沒有電話;而這一格又只顯示電話(REQ-20260819001),結果再次進入修改時看到的是
   * 「名稱 + 一個 —」,跟剛選定客戶當下顯示的內容對不起來。用 id 撈回主檔補齊,
   * 做法與 `ReceivablePanel` 的深連結同一套(REQ-20260815001)。
   *
   * 只補一次,而且只在使用者還沒動過這一格時補 —— 按了「重新選擇」換人或清空之後,
   * 不該被 effect 拉回原本的客戶。查無此人(items 空)就維持單據上的簡稱。
   *
   * 複製模式(REQ-20260911003)走同一條:代管客戶是從範本帶過來的,不補值的話
   * 複製出來的卡片一樣少編號與電話。
   */
  const consigneeLookup = usePartyById('customer', seed?.customerId ?? undefined);
  const consigneeHydrated = useRef(false);
  useEffect(() => {
    if (consigneeHydrated.current) return;
    const found = consigneeLookup.data?.items[0];
    if (!found || customer?.id !== seed?.customerId) return;
    consigneeHydrated.current = true;
    setCustomer(found);
  }, [customer, consigneeLookup.data, seed?.customerId]);

  /**
   * 廠商也一樣(REQ-20260902012)。單據沒存電話快照,`supplierFromOrder()` 只能給 null,
   * 所以編輯既有單據時卡片的「電話」永遠是一條橫線 —— 與剛選定廠商當下顯示的內容對不起來。
   * 做法與上面的代管客戶逐行相同(一次性旗標 + 使用者動過就不覆寫 + 查無資料維持簡稱)。
   *
   * ⚠️ **合併時保留單據上的收貨地址** —— 見 `supplierFromOrder()` 上方的註解:
   * 那是這張單當初談定的地址,不是主檔現值,不能因為補電話就被換掉。
   * 單據沒存地址時才用主檔的,免得整格空白。
   *
   * 複製模式(REQ-20260911003)同樣要補:不補的話複製出來的廠商卡片沒有電話。
   */
  const supplierLookup = usePartyById('supplier', seed?.supplierId ?? undefined);
  const supplierHydrated = useRef(false);
  useEffect(() => {
    if (supplierHydrated.current) return;
    const found = supplierLookup.data?.items[0];
    if (!found || supplier?.id !== seed?.supplierId) return;
    supplierHydrated.current = true;
    setSupplier((prev) => ({ ...found, address: prev?.address ?? found.address }));
  }, [supplier, supplierLookup.data, seed?.supplierId]);

  /**
   * 進貨類別選項(REQ-20260814002)。**取全部再濾掉停用的**,比照 `SalesOrderForm`
   * 的 `activeOrSelected()`:這張單原本的類別事後在設定頁被停用時,只列啟用中的會讓
   * 下拉跳到別的類別,使用者按存檔就把單據的類別默默改掉了。
   * 用 `all=true` 也與清單頁共用同一份快取(那邊要顯示停用類別建的歷史單)。
   */
  const { data: purchaseCategories, isLoading: categoriesLoading } = useReferences(
    'purchase-categories',
    true,
  );
  const categoryOptions = referenceNameMap(
    (purchaseCategories?.data ?? []).filter((c) => c.isActive || c.code === orderType),
  );
  const categoryCodes = Object.keys(categoryOptions);
  // 這裡原本有「沒選過就用清單第一筆」的 fallback(REQ-20260814002 的決定,那時清單
  // 只有兩筆、第一筆就是最大宗的紙張採購)。REQ-20260907001 依使用者指示改成另一邊:
  // 類別同時決定品項要填哪一組規格欄位(見 `work-order-category.ts` 的四個判斷式),
  // 而類別已被使用者自助加到多筆 —— 預設帶第一筆很容易整張單填完才發現類別是錯的。
  // 建單一律留空、要使用者手動選;編輯既有單仍帶回該單原本的類別(見上面的 useState)。
  /** 派工類才顯示生產指示欄位(REQ-20260818002)。判斷依據見 `isWorkOrderCategory()` */
  const isWorkOrder = isWorkOrderCategory(categoryOptions[orderType]);
  /**
   * 印刷顏色 / 加工這兩格看的是另一個判斷式(REQ-20260821005)——
   * 「外包加工」這種不含「派工」的類別也要填,但它不該連帶打開整組派工欄位群。
   */
  const showsProcessing = showsProcessingFields(categoryOptions[orderType]);
  /**
   * 外包加工類的品項換一套版面(REQ-20260821006):品名改吃「印刷加工」的建議清單、
   * 規格 → 加工規格、數量單位 → 單位且移到數量後面,紙張維度那一整列整個不顯示。
   *
   * ⚠️ 這個判斷式**排除派工類**(見 `isOutsourceProcessingCategory()`)——
   * 派工單的「尺寸 / 開數」就是紙本的紙張尺寸,列印頁在讀它,不能跟著藏。
   */
  const isOutsourceProcessing = isOutsourceProcessingCategory(categoryOptions[orderType]);
  /**
   * 商品採購類的品項再換一套版面(REQ-20260904003):進的是成品而不是紙,
   * 紙張規格那一整組不渲染,第一組精簡成八格(多了材質描述、單位改成挑選框)。
   *
   * ⚠️ 關鍵字是**「商品」不是「採購」** —— 預設類別就叫「紙張採購」,
   * 用「採購」比對會把正式環境最大宗的紙張材料單一起換掉(見 `isGoodsPurchaseCategory()`)。
   */
  const isGoodsPurchase = isGoodsPurchaseCategory(categoryOptions[orderType]);
  /**
   * 運輸費用類**整張表單換版面**(REQ-20260907002):單據資訊 / 付款方式 /
   * 運費明細 / 金額與結帳 / 成本歸屬 / 單據附件六張卡片,依使用者附圖排。
   *
   * ⚠️ 與上面四個判斷式不同:那四個換的是**品項版面**,這一個連單頭與合計區
   * 都換掉。所以下面的 JSX 是「運費類渲染這一組、其餘類別渲染原本那一組」,
   * **其他類別的版面一格都沒有動**。
   */
  const isFreight = isFreightCategory(categoryOptions[orderType]);

  /**
   * 商品採購版面的「單位」清單(REQ-20260904003)。使用者指名沿用他已經在維護的
   * **設定 → 系統設定 → 貨品單位**(`product-units`),不是新開一張 reference ——
   * REQ-20260824001 §2.2 / REQ-20260902003 §2.1 否決的是「為進貨單位另做一張表」,
   * 沿用一份既有清單不衝突。
   *
   * **不傳 `all=true`**:這是建單用的建議清單,停用的單位不該再被選到
   * (與進貨類別那一份刻意不同 —— 那份要讓舊單的停用類別顯示得出來)。
   */
  const { data: unitOpts } = useReferences('product-units');

  /**
   * 印刷顏色 / 後製加工的建議清單(REQ-20260810001 的既有 reference)。
   * **只是建議、仍可手打**:派工單上寫的是「特色 + 燙金」「打孔:1 孔孔徑 3mm」
   * 這種現場組合,做成值域就填不進去。沿用藝品印刷訂單那套 input + datalist。
   */
  const { data: printColorOpts } = useReferences('print-color-options', false);
  const { data: processingOpts } = useReferences('processing-options', false);

  /**
   * 「工單 / 案號」的建議清單(REQ-20260819002)。填在這一格的其實是**代管客戶的
   * 應收單號** —— 進的這批貨是為了哪張出貨單而叫的。所以選了代管客戶之後,
   * 就把那位客戶最近的單號列出來給人挑,不必再開一個分頁去查。
   *
   * 沒選代管客戶時 `enabled: false`,一次請求都不發 —— `customerId` 是 undefined 的話
   * 後端會回「全部訂單」,那份清單對這張單毫無意義(見 `SalesOrderFilter.enabled`)。
   *
   * 一張單只查一次,不是每個品項各查一次:建議清單依單頭的代管客戶而定,
   * 各品項只是共用同一份清單,選的值仍然各自獨立。
   * API 已依 `txnDate desc, id desc` 排序,取前 10 筆就是「最近登錄的 10 筆」。
   *
   * 清單上顯示的是「單號 - 品名 等N項」(REQ-20260819003)—— 光看單號分不出是哪張單,
   * 容易選錯。選定後再從 `display` 反查回純單號存進欄位(見下方 onChange),
   * 存進資料庫的仍然只有單號本身。品名是 `list()` 順便帶回來的,不必逐筆再打一次 API。
   *
   * 選完之後**已選的 chip 也顯示同一行完整字串**(REQ-20260905001):
   * 原本會縮回純單號,使用者看不出自己選到的是哪張單。反查是用 `orderNo` → `display`,
   * 送出的值一行都沒變。REQ-20260905003 改成複選之後這兩層對應原封不動,
   * 只是從「一個值」變成「一個陣列各自對應」。
   *
   * ⚠️ **這一份只餵非運費類的品項**。運費明細那一格的清單依「該列的送達客戶」
   * 而定(REQ-20260908001,見 `FreightWorkOrderPicker`)—— 運費版面不渲染代管客戶,
   * 沿用這一份的結果是永遠「已選 0/0」。
   */
  const { data: consigneeOrders } = useSalesOrders({
    customerId: customer?.id,
    enabled: customer != null,
  });
  const workOrderNoOptions = (consigneeOrders?.items ?? []).slice(0, 10).map((o) => ({
    orderNo: o.orderNo,
    display: o.firstProductName
      ? `${o.orderNo} - ${o.firstProductName}${o.lineCount > 1 ? ` 等${o.lineCount}項` : ''}`
      : o.orderNo,
  }));

  /**
   * 單頭金額。運費類多帶一個 `linePaymentType` —— 到付的列不計入
   * (REQ-20260907002,算式在 `calcPurchaseTotals()`,前後端共用同一支)。
   * 非運費類一律帶 `null`,**與改動前逐字相同**。
   */
  const totals = useMemo(
    () =>
      calcPurchaseTotals({
        lines: lines.map((l) => ({
          qty: l.qty,
          unitPrice: l.unitPrice,
          taxCategory: l.taxCategory || null,
          linePaymentType: isFreight ? effectiveLinePayment(l, freightPaymentMode) : null,
        })),
        taxCategory,
        unitPriceTaxInclusive,
      }),
    [lines, taxCategory, unitPriceTaxInclusive, isFreight, freightPaymentMode],
  );

  /**
   * 「依付款方式拆分」的三個數字(REQ-20260907002)。
   *
   * 做法是**拿同一支 `calcPurchaseTotals()` 各算一次子集合**,不另外發明算法:
   * 稅額進位到整數元這件事必須逐桶各自成立,自己乘 1.05 會跟合計對不起來。
   * 子集合裡**不帶 `linePaymentType`** —— 到付那一桶要算得出自己的金額,
   * 排除規則只作用在單頭那一次。
   */
  const freightSplit = useMemo(() => {
    const bucket = (type: PurchaseLinePaymentType) =>
      calcPurchaseTotals({
        lines: lines
          .filter((l) => effectiveLinePayment(l, freightPaymentMode) === type)
          .map((l) => ({
            qty: l.qty,
            unitPrice: l.unitPrice,
            taxCategory: l.taxCategory || null,
          })),
        taxCategory,
        unitPriceTaxInclusive,
      });
    return { MONTHLY: bucket('MONTHLY'), CASH: bucket('CASH'), COLLECT: bucket('COLLECT') };
  }, [lines, freightPaymentMode, taxCategory, unitPriceTaxInclusive]);

  /**
   * 「待補」清單(REQ-20260907002)。缺託運單號或缺歸屬工單的列都算 ——
   * 那兩格是事後對帳與成本歸屬的唯一依據,補不齊就不該入帳。
   *
   * ⚠️ **到付的列不要求歸屬工單**:那筆錢不是我方的成本(畫面上顯示「不計成本」),
   * 沒有工單可歸。託運單號仍然要 —— 到付一樣要對得回貨運行的班次。
   */
  const freightPending = useMemo(() => {
    if (!isFreight) return [] as string[];
    return lines.flatMap((l) => {
      const day = shortMd(l.shipDate);
      const missing: string[] = [];
      if (!l.trackingNo.trim()) missing.push(`${day} 託運單號`);
      if (effectiveLinePayment(l, freightPaymentMode) !== 'COLLECT' && l.workOrderNos.length === 0) {
        missing.push(`${day} 歸屬工單`);
      }
      return missing;
    });
  }, [isFreight, lines, freightPaymentMode]);

  /**
   * 選定廠商 → 建單時把單頭稅別預設成該廠商主檔的「稅別」(REQ-20260902009,
   * 值域自 REQ-20260902011 起兩邊一致,所以是直接帶值而不是對照)。
   *
   * ⚠️ **只在建立模式**。編輯模式的 PartyPicker 是 `disabled` 唯讀,而且 PATCH 是
   * 整張覆寫 —— 動到稅別等於把舊單的稅額與總金額默默改掉。
   *
   * 換廠商時**會覆寫使用者已經手動改過的選擇**,這是刻意的:選廠商是明確動作,
   * 換了廠商預設就該跟著新廠商走。選完之後那顆下拉照樣改得動,系統不會再蓋回去
   * (這支只在 `onChange` 觸發,不是 effect)。
   *
   * 廠商沒填稅別或清空廠商時**不動**這一格 —— 用「沒有資訊」覆寫「有資訊」,
   * 會把使用者剛選好的免稅吃掉。
   */
  const handleSupplierChange = (next: PartyInfo | null): void => {
    setSupplier(next);
    if (isEdit || next == null) return;
    if (next.taxMode != null) setTaxMode(next.taxMode);
  };

  const setLine = (index: number, patch: Partial<LineDraft>): void => {
    setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  };

  /**
   * 託運單號掃描(REQ-20260907003)。狀態以列索引為 key,只活在畫面上。
   *
   * ⚠️ 掃描用的圖**不上傳、不落地** —— 送去辨識、拿到文字就丟。要留存單據仍走
   * 這一列的「憑證」或表單最下方的單據附件卡片。
   */
  const [trackingScan, setTrackingScan] = useState<Record<number, TrackingScanState>>({});
  /**
   * 目前展開分攤 / 去向面板的那一列(REQ-20260908002)。**一次只開一列** ——
   * 面板是「這一列的錢怎麼分」,同時開兩列只會讓人分不清在改哪一列。
   */
  const [expandedLine, setExpandedLine] = useState<number | null>(null);
  const scanInputs = useRef<Record<number, HTMLInputElement | null>>({});

  /**
   * 品項那一排的四格輸入框(REQ-20260911001)。**每列一份**,以列索引為 key ——
   * 品項是可增列 / 刪列的多列表格,單一 `RefObject` 只夠一列用。
   * 寫法比照上面的 `scanInputs`;刪列後留在 Record 裡的那筆會被下一次 render 的
   * callback ref 設成 null,跳格時 `?.focus()` 直接無事發生。
   */
  const qtyInputs = useRef<Record<number, HTMLInputElement | null>>({});
  const unitInputs = useRef<Record<number, HTMLInputElement | null>>({});
  const unitPriceInputs = useRef<Record<number, HTMLInputElement | null>>({});
  const amountInputs = useRef<Record<number, HTMLInputElement | null>>({});

  const setScan = (index: number, next: TrackingScanState): void => {
    setTrackingScan((prev) => ({ ...prev, [index]: next }));
  };

  /**
   * 把辨識到的單號填進第 index 列。欄位**已經有值**且不一樣時先問一次 ——
   * 二次確認沿用 REQ-20260819007 立下的慣例(會蓋掉使用者輸入的動作要先確認)。
   * 使用者按取消就什麼都不改,回傳 false。
   */
  const applyScannedTrackingNo = (index: number, trackingNo: string): boolean => {
    const current = lines[index]?.trackingNo.trim() ?? '';
    if (
      current.length > 0 &&
      current !== trackingNo &&
      !window.confirm(`此列託運單號已經是「${current}」,要換成掃描到的「${trackingNo}」嗎?`)
    ) {
      return false;
    }
    setLine(index, { trackingNo });
    return true;
  };

  const onScanTrackingNo = async (index: number, file: File | undefined): Promise<void> => {
    if (!file) return;
    setScan(index, { ...IDLE_SCAN, scanning: true });
    try {
      const result = await scanTrackingNo(file);
      if (result.status !== 'ok') {
        // 認不出來 / 沒設定 AI / 模型不支援讀圖 → 紅字提示,**欄位保持原樣**
        setScan(index, { ...IDLE_SCAN, error: result.message ?? '辨識失敗' });
        return;
      }
      // 多筆候選**不自動選第一個**:每家物流的單據格式都不同,猜錯比留白糟
      if (result.candidates.length > 1) {
        setScan(index, { ...IDLE_SCAN, candidates: result.candidates });
        return;
      }
      const only = result.candidates[0];
      setScan(index, {
        ...IDLE_SCAN,
        filled: only != null && applyScannedTrackingNo(index, only.trackingNo),
      });
    } catch (err) {
      setScan(index, {
        ...IDLE_SCAN,
        error: getApiErrorMessage(err, err instanceof Error ? err.message : '辨識失敗'),
      });
    } finally {
      // 清掉 input 的值,同一張照片才能再掃一次(同 DocumentAttachmentCard 的做法)
      const input = scanInputs.current[index];
      if (input) input.value = '';
    }
  };

  const onPickScanCandidate = (index: number, trackingNo: string): void => {
    if (applyScannedTrackingNo(index, trackingNo)) {
      setScan(index, { ...IDLE_SCAN, filled: true });
    }
  };

  /**
   * 數量 / 單價改了 → 總價跟著算(REQ-20260821005)。
   * 公式走 `calcLineAmount()` —— 與下方合計區、後端 `calcPurchaseTotals` 同一支,
   * 不另外發明算法(進貨沒有才數 / 模數 / 折扣,所以只餵 qty 與 unitPrice)。
   */
  const setLinePricing = (index: number, patch: { qty?: string; unitPrice?: string }): void => {
    setLines((prev) =>
      prev.map((l, i) => {
        if (i !== index) return l;
        const next = { ...l, ...patch };
        return { ...next, amount: calcLineAmount({ qty: next.qty, unitPrice: next.unitPrice }) };
      }),
    );
  };

  /**
   * 數量單位(REQ-20260824001 時叫「採購單位」)改了 → 選到「令」時把換算率代成 500。
   * 右邊的「庫存單位數量」讀 `calcStockUnitQty(qty, conversionRate)`,所以代入之後
   * 「5 令 × 500 = 2500 張入庫」自己就算出來了,不必另外算一份。
   *
   * ⚠️ **只在使用者還沒動過換算率時代入**:判準是那一格仍然等於預設值
   * `EMPTY_LINE.conversionRate`('1')。手動填過(哪怕填的正好也是 1 以外的值)
   * 就不覆寫 —— 換算率是會計自己按實際規格填的,被系統默默改掉比沒有自動代入更糟。
   */
  const setLineUnitName = (index: number, unitName: string): void => {
    setLines((prev) =>
      prev.map((l, i) => {
        if (i !== index) return l;
        const untouchedRate = l.conversionRate === EMPTY_LINE.conversionRate;
        return {
          ...l,
          unitName,
          conversionRate:
            untouchedRate && unitName === '令' ? REAM_CONVERSION_RATE : l.conversionRate,
        };
      }),
    );
  };

  /**
   * 反過來:填總價 → 反推單價 = 總價 ÷ 數量。**單價才是送出去的值**,
   * 所以一定要寫回 `unitPrice`(下方合計區也是讀它重算,填完馬上看得到實際總額)。
   *
   * 數量是 0 或空字串時不能除 —— 維持原單價,輸入框照樣打得動,不卡死。
   * 反推的單價取 4 位小數(`toUnitPriceString()`),與 DB 的 `Decimal(14,4)` 一致
   * (REQ-20261001001:原本取兩位,1000 張共 603 元反推成 0.60,入帳變 600)。
   * 除不盡時(10 ÷ 3)存的是四捨五入後的單價,合計區顯示的就是實際會入帳的金額。
   */
  const setLineAmount = (index: number, amount: string): void => {
    setLines((prev) =>
      prev.map((l, i) => {
        if (i !== index) return l;
        const qty = toNumber(l.qty);
        if (qty === 0) return { ...l, amount };
        return { ...l, amount, unitPrice: toUnitPriceString(toNumber(amount) / qty) };
      }),
    );
  };

  /**
   * 「這張表單有沒有被改過」(REQ-20260917002)。
   *
   * 這裡**自己算一份**,不是沿用什麼現成的訊號 —— 前兩次做同一件事時
   * (REQ-20260819011 客戶 / 廠商操作台、REQ-20260825009 編輯訪談)一邊有頁面既有的
   * `dirty` state、一邊有 react-hook-form 的 `formState.isDirty`,而這張表單兩者都沒有:
   * 每一格都是獨立的 `useState`。做法是把**送出時會讀到的那些 state** 序列化,
   * 跟掛載當下的那一份比字串。兩份由同一段程式產生,key 順序必然相同。
   *
   * 幾個刻意的取捨:
   *  - 三張廠商 / 客戶卡片**只取 id**:payload 認的就是 id,而它們會被上面兩支
   *    hydration effect 在掛載後補上主檔的電話 / 地址 —— 整個物件拿去比對的話,
   *    表單一打開就會自己變成「已修改」。收貨地址是跟著廠商走的快照,
   *    沒有獨立入口可以改它(編輯模式的廠商卡片還是 `disabled`)。
   *  - 沒有輸入入口的那幾格(發票 / 入庫方式 / 付款方式 / 預計付款日)不列入:
   *    它們連 setter 都沒有,值不可能變。
   *  - 純畫面狀態不列入:錯誤訊息、提醒視窗、託運單號掃描、展開的那一列。
   *  - **已上傳的附件不列入**:那是上傳即存(presign 需要 docId),不走這張表單的
   *    payload,傳完就已經在系統裡,不是「還沒儲存的東西」。
   *    但**建單模式先選好、還沒傳的暫存檔算**(REQ-20261012001)—— 那正是
   *    「關掉就沒了」的東西。它們是 `File` 物件序列化不了,所以不進 snapshot,
   *    直接看有沒有。
   */
  const snapshot = useMemo(
    () =>
      JSON.stringify({
        supplierId: supplier?.id ?? null,
        customerId: customer?.id ?? null,
        paperSupplierId: paperSupplier?.id ?? null,
        pickupMethod,
        orderType,
        txnDate,
        receivedDate,
        dueDate,
        taxMode,
        notes,
        statementNo,
        freightPaymentMode,
        cashSource,
        payerName,
        costAllocationMode,
        lines,
      }),
    [
      supplier,
      customer,
      paperSupplier,
      pickupMethod,
      orderType,
      txnDate,
      receivedDate,
      dueDate,
      taxMode,
      notes,
      statementNo,
      freightPaymentMode,
      cashSource,
      payerName,
      costAllocationMode,
      lines,
    ],
  );
  /** 掛載當下那一份,只取第一次。存檔成功後父層會收掉表單,元件卸載 → 不必重設 */
  const initialSnapshot = useRef<string | null>(null);
  if (initialSnapshot.current == null) initialSnapshot.current = snapshot;
  const dirty =
    initialSnapshot.current !== snapshot ||
    pendingDocFiles.length > 0 ||
    pendingWorkOrderFiles.length > 0;

  /**
   * 忘記按儲存就關分頁 / 重新整理時提醒(REQ-20260917002 需求第 2 點)。
   * 提示文字由瀏覽器決定、不可自訂(規範限制,見 REQ-20260804002)。
   *
   * **只做 `beforeunload`,不做 `useBlocker`** —— 那支需要 data router,而這張表單的
   * 測試是直接 `render(<PurchaseOrderForm ... />)`、外面連 router 都沒有,掛上去會讓
   * 整個測試檔一起 throw。站內換頁的攔截這次不做。
   */
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  /**
   * 有未儲存變更才問一次(REQ-20260917002)。沒改過就直接關掉,問了只是多按一次。
   * 文案與 `ConfirmDialog` 的用法沿用 REQ-20260916004 的客戶 / 廠商操作台,
   * 不用 `window.confirm`(REQ-20260903009 / REQ-20260907004 立下的規矩)。
   */
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const handleCancel = (): void => {
    if (!dirty) {
      onCancel();
      return;
    }
    setCancelConfirm(true);
  };

  /**
   * 建單成功後把建單前先選好的附件逐一傳上去(REQ-20261012001),回傳沒傳成功的檔名。
   *
   * **一個一個傳**而不是 `Promise.all`:手機網路一次只佔一條比較穩,失敗也對得到是哪一個。
   * 單一檔案失敗**不中斷、不往外拋** —— 單已經建好了,讓錯誤冒出去的話使用者會再按一次,
   * 那是重送 create、重複建單。失敗的檔名交給附件步驟,讓使用者在那裡補傳。
   */
  const uploadPendingFiles = async (orderId: number): Promise<string[]> => {
    const failed: string[] = [];
    const groups = [
      ['PURCHASE_ORDER', pendingDocFiles],
      ['PURCHASE_ORDER_WORK_ORDER', pendingWorkOrderFiles],
    ] as const;
    for (const [docType, files] of groups) {
      for (const file of files) {
        try {
          await uploadDocumentAttachment(docType, orderId, file);
        } catch {
          failed.push(file.name);
        }
      }
    }
    return failed;
  };

  /**
   * @param thenSubmit 存檔成功後接著打 `POST /:id/submit`(運費表單的「確認入帳」)。
   *                   一般的「儲存草稿 / 儲存修改」不帶,行為與改動前逐字相同。
   */
  const submitOrder = (thenSubmit = false): void => {
    setFormError(null);
    if (!supplier) {
      setFormError('請先選擇廠商');
      return;
    }
    // 「沒選類別」自 REQ-20260907001 起是**正常的初始狀態**,所以兩種情形要分開:
    // 清單裡有東西、只是還沒選 → 單純提醒選一下;清單根本是空的(設定頁全被停用 /
    // 還沒載完)→ 沿用原本指路設定頁的那句,不然是叫使用者去選他選不到的東西。
    // (兩種都不放行:送出去只會拿到 zod 的「至少 1 個字元」,看不出該去哪裡處理)
    if (!orderType) {
      setFormError(
        categoryCodes.length > 0
          ? '請先選擇進貨類別'
          : '請先選擇進貨類別(可在「設定 → 系統設定 → 進貨類別」新增)',
      );
      return;
    }

    const input = {
      supplierId: supplier.id,
      orderType,
      customerId: customer?.id ?? null,
      paperSupplierId: paperSupplier?.id ?? null,
      // 這格沒有 UI,建單一律 null;但 PATCH 是整張覆寫,編輯時要把單據原值送回去,
      // 否則來源訪談(代管庫存拋轉時的商品圖片來源)會被默默切斷。
      // ⚠️ 複製模式讀的仍是 `editOrder` 而不是 `seed`:來源訪談是**上一張單**的,
      // 帶過來等於把新單掛到舊訪談底下(REQ-20260911003)
      interviewId: editOrder?.interviewId ?? null,
      txnDate,
      receivedDate: receivedDate || null,
      dueDate: dueDate || null,
      receivingAddress: supplier.address ?? null,
      pickupMethod: pickupMethod || null,
      invoiceNo: invoiceNo || null,
      invoiceDate: invoiceDate || null,
      taxCategory,
      unitPriceTaxInclusive,
      stockInType,
      paymentMethod: paymentMethod || null,
      expectedPaymentDate: expectedPaymentDate || null,
      expectedPaymentDateReason: expectedPaymentDateReason || null,
      notes: notes || null,
      // 運輸費用單的單頭欄位(REQ-20260907002)。其他類別沒有入口 →
      // state 維持初始值 → 全部送 null,payload 與改動前逐欄相同
      statementNo: statementNo || null,
      freightPaymentMode: freightPaymentMode || null,
      cashSource: cashSource || null,
      payerName: payerName || null,
      costAllocationMode: costAllocationMode || null,
      lines: lines.map((l) => ({
        productCode: 'MANUAL',
        // 運費明細沒有「品名」這一格(附圖),但 `productName` 是必填 ——
        // 補成「運費」,與出貨自動產生的那條路徑用同一個字
        // (REQ-20260829003 的 `createFreightOrder()`),對帳時兩邊看起來一致
        productName: isFreight ? l.productName || '運費' : l.productName,
        productSpec: l.productSpec || null,
        // 材質描述(REQ-20260904003)。只有商品採購版面填得到,其餘類別一律 null
        productMaterial: l.productMaterial || null,
        unitName: l.unitName || null,
        qty: l.qty,
        // 空白 = 還沒 KEY(商品還沒到、金額未定),草稿先記 0,進貨到料時回來補
        // (REQ-20260917001)。⚠️ 放寬的是**這個呼叫端**不是契約:
        // `PurchaseOrderLineInputSchema.unitPrice` 仍然是必填的金額字串,
        // 改成 optional 會讓後端與未來的匯入一起失去驗證。
        unitPrice: l.unitPrice.trim() || '0',
        // 紙張顏色 / 厚度單位(REQ-20260902003)。沒填就是 null ——
        // 厚度單位的 null 是「未指定」,不會被補成 'gsm'
        paperColor: l.paperColor || null,
        weightGsm: l.weightGsm ? Number(l.weightGsm) : null,
        weightUnit: l.weightUnit || null,
        sizeSpec: l.sizeSpec || null,
        grainDirection: l.grainDirection || null,
        conversionRate: l.conversionRate || null,
        taxCategory: l.taxCategory || null,
        // 工單 / 案號送陣列(REQ-20260905003)。一張都沒挑就是空陣列,不是 null
        workOrderNos: l.workOrderNos,
        // 運費是費用不是庫存品 —— 比照出貨自動產生的那張單(REQ-20260829003)
        // 一律 `DIRECT_TO_WORK_ORDER`,不會參與代管庫存拋轉。
        // (`stockInType` 仍是 OWNED,所以不會撞上 `assertDirectShip()` 的工單號必填)
        costDestination: isFreight ? 'DIRECT_TO_WORK_ORDER' : l.costDestination,
        printColor: l.printColor || null,
        specialItems: l.specialItems || null,
        colorProofRequired: l.colorProofRequired,
        impressionQty: l.impressionQty ? Number(l.impressionQty) : null,
        finishedSize: l.finishedSize || null,
        finishedQty: l.finishedQty ? Number(l.finishedQty) : null,
        postProcessing: l.postProcessing || null,
        // 沒填 = 尚未回報 → null。0 是「一片都沒交回」,兩者不能混(REQ-20260821006)
        completedQty: l.completedQty || null,
        // ── 運輸費用單的明細欄位(REQ-20260907002)。非運費類沒有入口 → 一律 null
        shipDate: isFreight ? l.shipDate || null : null,
        trackingNo: isFreight ? l.trackingNo || null : null,
        deliveryCustomerId: isFreight ? (l.deliveryCustomer?.id ?? null) : null,
        // 存的是**生效後**的值(見 `effectiveLinePayment()` 的註解)
        linePaymentType: isFreight ? effectiveLinePayment(l, freightPaymentMode) : null,
        // ── 分攤(REQ-20260908002)。非運費類沒有入口 → 一律 null / 不帶。
        //    工單清單仍然只有 `workOrderNos` 一份,這裡只是依單號帶金額
        allocationMethod: isFreight ? l.allocationMethod || null : null,
        ...(isFreight && l.allocationMethod
          ? {
              workOrderAllocations: l.workOrderNos
                .filter((no) => l.workOrderAmounts[no] != null)
                .map((no) => ({ workOrderNo: no, amount: l.workOrderAmounts[no] ?? '0' })),
            }
          : {}),
      })),
    } as CreatePurchaseOrderInput;

    if (editOrder) {
      // 更新沒有 supplierId(廠商不可換),schema 也是 omit 掉它的那一份
      const { supplierId: _supplierId, ...rest } = input;
      const parsedUpdate = UpdatePurchaseOrderSchema.safeParse(rest);
      if (!parsedUpdate.success) {
        setFormError(describeZodError(parsedUpdate.error, FIELD_LABELS));
        return;
      }
      updateMutation.mutate(
        { id: editOrder.id, ...parsedUpdate.data },
        {
          onSuccess: (order) => {
            if (!thenSubmit) {
              onCreated(order.orderNo);
              return;
            }
            submitMutation.mutate(order.id, {
              onSuccess: () => onCreated(order.orderNo),
              onError: (err) => setFormError(getApiErrorMessage(err, '送出確認失敗')),
            });
          },
          // 後端擋下來(不是草稿、發票號碼重複、直送單缺工單號)的訊息要原樣透出
          onError: (err) => setFormError(getApiErrorMessage(err, '儲存失敗')),
        },
      );
      return;
    }

    const parsed = CreatePurchaseOrderSchema.safeParse(input);
    if (!parsed.success) {
      setFormError(describeZodError(parsed.error, FIELD_LABELS));
      return;
    }

    createMutation.mutate(parsed.data, {
      onSuccess: (order) => {
        const proceed = (failedUploads: string[]): void => {
          // 有檔案沒傳上去時,「確認入帳」也**不接著送出**、改停在附件步驟 ——
          // 送出確認後單就凍結(R-12 + R-08),那些檔案就再也補不進來了
          if (!thenSubmit || failedUploads.length > 0) {
            setCreatedOrder({ id: order.id, orderNo: order.orderNo, failedUploads });
            return;
          }
          submitMutation.mutate(order.id, {
            onSuccess: () => onCreated(order.orderNo),
            onError: (err) => setFormError(getApiErrorMessage(err, '送出確認失敗')),
          });
        };
        // 沒有暫存檔:同步往下走,行為與改動前逐字相同
        if (pendingDocFiles.length === 0 && pendingWorkOrderFiles.length === 0) {
          proceed([]);
          return;
        }
        // 建單前先選好的附件(REQ-20261012001)**一定要在送出確認之前傳完** ——
        // 後端只收草稿的附件
        setUploadingPending(true);
        void uploadPendingFiles(order.id).then((failedUploads) => {
          setUploadingPending(false);
          // 已經進系統(或已列進失敗清單)了,不再掛著「待上傳」
          setPendingDocFiles([]);
          setPendingWorkOrderFiles([]);
          proceed(failedUploads);
        });
      },
      onError: (err) => setFormError(err instanceof Error ? err.message : '建立失敗'),
    });
  };

  /**
   * 沒填單位的品項是第幾列(REQ-20260911001 需求第 3 點)。**1-based**,
   * 直接拿去寫進提醒視窗的內文。
   *
   * 運費類**整組排除**:那個版面根本沒有「單位」這一格(運費是一列一筆金額),
   * 算進去的話每一列都會被判定沒填,而使用者在畫面上找不到任何地方可以補 ——
   * 一個補不了的提醒等於把「儲存草稿 / 確認入帳」變成每次都要按兩下。
   */
  const linesMissingUnit = isFreight
    ? []
    : lines.flatMap((l, i) => (l.unitName.trim() === '' ? [i + 1] : []));

  /**
   * 沒填單價的品項是第幾列(REQ-20260917001)。**1-based**,同上。
   *
   * ⚠️ **只算空字串,0 不算** —— 這與該格底下那行 inline 提示的判定**刻意不同**:
   *  - inline 提示看「空字串**或** 0」:重開已存的草稿時單價是 `'0'` 而不是空白
   *    (送出時空白會被存成 0),只看空字串的話提醒一重開就消失了。
   *  - 這個視窗只看空字串:0 是合法單價(贈品 / 樣品 / 到付不計成本),把 0 算進來
   *    等於每次存檔都跳視窗、每次都要按兩下,提醒就變成噪音。
   *
   * 運費版面**不排除**(與 `linesMissingUnit` 不同):運費那一格叫「金額」但
   * 使用者填得到也改得掉,不像單位那樣在畫面上根本沒有入口 —— 排除的話,
   * 金額空白的運費列會靜默存成 0。
   */
  const linesMissingPrice = lines.flatMap((l, i) => (l.unitPrice.trim() === '' ? [i + 1] : []));

  /**
   * 送出的守門(REQ-20260911001;REQ-20260917001 起同時涵蓋單價)。掛在 `onSubmit`
   * 而不是 `<form onSubmit>` 上,所以「建立草稿 / 儲存修改」「儲存草稿」「確認入帳」
   * 三條路徑都吃得到。
   *
   * 單位在 zod / DB 上**仍然是選填**(`unitName` optional/nullable,舊單很多沒填)——
   * 這是提醒不是驗證,按「仍要儲存」就照原本的流程走。單價則是送出時補成 `'0'`
   * (見 payload 那一段),契約本身沒有放寬。
   *
   * **兩件事共用同一個視窗**,不開第二個 ConfirmDialog:兩個提醒各跳一次的話,
   * 兩格都沒填就要按四下才存得起來。
   *
   * **刻意不記「已經確認過」**(比照 REQ-20260903009 §三):確認視窗的「仍要儲存」
   * 是一次性地直接呼叫 `submitOrder()`,不是先解鎖再送出。留一格持久旗標的話,
   * 「確認過 → 又新增一列沒填單位 → 再送出」會靜默放行。
   *
   * 廠商 / 類別沒選時**不攔** —— 那兩個是真的擋下來的錯誤,先讓 `submitOrder()`
   * 把紅字寫出來,不要用一個「要不要存」的問句蓋掉「你還沒選廠商」。
   */
  const onSubmit = (thenSubmit = false): void => {
    if (supplier && orderType && (linesMissingUnit.length > 0 || linesMissingPrice.length > 0)) {
      setUnitConfirm({ thenSubmit });
      return;
    }
    submitOrder(thenSubmit);
  };

  if (createdOrder) {
    return (
      <PurchaseOrderAttachmentStep
        order={createdOrder}
        failedUploads={createdOrder.failedUploads}
        onDone={() => onCreated(createdOrder.orderNo)}
      />
    );
  }

  const taxInclusiveOutOfTolerance =
    unitPriceTaxInclusive &&
    Math.abs(Number(totals.taxInclusiveDiff)) > PURCHASE_TAX_INCLUSIVE_TOLERANCE;

  return (
    <form
      className="grid gap-4 bg-white shadow-sm rounded-md p-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      // 打單過程中的 Enter 一律不送出(REQ-20260911001)—— 見 `blockImplicitSubmit()`。
      // 存檔只剩下方的「建立草稿 / 儲存修改」「儲存草稿」「確認入帳」三顆鈕
      onKeyDown={blockImplicitSubmit}
    >
      {/*
        運費類的頂部提醒列(REQ-20260907002 附圖最上面那一條)。單號沿用既有的
        `OrderNoBlock`(只是收掉底線讓它排得進這一條),不另外做一個。
        「待補」的定義見 `freightPending` —— 補齊之前「確認入帳」按不下去,
        但「儲存草稿」永遠可以按:草稿本來就是拿來邊做邊存的。
      */}
      {isFreight ? (
        <div
          className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2"
          data-testid="po-freight-banner"
        >
          <span className="text-sm text-amber-800">
            {freightPending.length > 0 ? (
              <>
                草稿 · 尚有 <strong>{freightPending.length} 項</strong> 待補才能入帳
              </>
            ) : (
              <>草稿 · 可以入帳</>
            )}
          </span>
          <OrderNoBlock
            orderNo={editOrder?.orderNo ?? previewOrderNo}
            isPreview={!isEdit}
            bare
          />
        </div>
      ) : (
        <OrderNoBlock orderNo={editOrder?.orderNo ?? previewOrderNo} isPreview={!isEdit} />
      )}

      {/* 這家廠商「下次要注意的事」(REQ-20260918002)。唯讀、可收合、**不擋送出** ——
          提醒的是填單前要知道的規則,不是送出前的防呆(理由見 REQ-20260918002)。
          還沒選到廠商時 `ownerId` 是 undefined —— 元件不打 API 也不顯示。 */}
      <OpenRemindersBanner ownerType="supplier" ownerId={supplier?.id} />

      {/* 複製模式的提示(REQ-20260911003)。把「哪些東西刻意沒帶過來」講清楚 ——
          發票號碼 / 對帳單號 / 交件日期在非運費類的版面上**根本沒有輸入格**,
          不寫出來使用者沒有別的地方看得到;工單 / 案號有格子但它是空的,
          而空著送出多數類別**不會被擋**,所以更要提醒。 */}
      {copyFrom ? (
        <div
          className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
          data-testid="po-copy-hint"
        >
          本單複製自 <span className="font-mono font-medium">{copyFrom.orderNo}</span>
          。發票號碼、對帳單號、交件日期與品項的工單 / 案號沒有帶入,請重新確認;附件不會複製。
        </div>
      ) : null}

      {/* 單頭四格的順序自 REQ-20260905006 起是 進貨類別 / 採購日期 → 廠商 / 代管客戶
          (改動前是 廠商卡片 → 類別 → 日期 → 代管客戶)。**類別排第一格**是因為它決定
          下面品項要填哪些規格欄位 —— 先選類別再往下填,不必填到一半才發現版面換了。
          格線仍是既有的 `md:grid-cols-2`,沒有另開一條。 */}
      {/* 運費類多一個卡片標題與外框(REQ-20260907002 附圖「單據資訊」那張卡)。
          其餘類別的 class 逐字不變 —— 這一格的四個欄位本來就是同一組。 */}
      {isFreight ? (
        <p className="text-sm font-medium text-slate-700 -mb-2">單據資訊</p>
      ) : null}
      <div
        className={`grid gap-4 md:grid-cols-2${isFreight ? ' border rounded-md p-3' : ''}`}
        data-testid="po-header"
      >
        {/* placeholder **一定要給**(REQ-20260907001):建單時 `orderType` 是空字串,
            原生 select 找不到 value 相符的 option 就會顯示第一個選項的文字 ——
            看起來像已經選好了,但送出時會被上面的守門擋下。清單還沒回來 / 全被停用時
            沿用原本的說明文字(那兩種情況本來就選不到東西)。 */}
        <Select
          id="po-type"
          label="進貨類別 *"
          value={orderType}
          onChange={setOrderType}
          options={categoryOptions}
          placeholder={
            categoryCodes.length > 0
              ? '— 請選擇 —'
              : categoriesLoading
                ? '載入中...'
                : '尚未設定進貨類別'
          }
          hint={isFreight ? '費用類 · 不進庫存' : '決定下方要填哪些規格欄位'}
        />

        {/* 這一格自 REQ-20260916001 起標「採購日期」(原本叫「進貨日期」)——
            **欄位本身沒換**,存的仍然是 `txnDate`,單號的年月日、應付月份與預計付款日
            也仍然全部取它。改名是因為它指的是「跟廠商下訂那天」,而使用者要記的
            「商品實際到貨入庫」是右邊那一格(`receivedDate`),兩者常常不同天。

            運費類仍然標「帳單日期」(REQ-20260907002 附圖):貨運行的月結單是一整個月
            的班次,各趟的日期在明細的「運送日」,沒有「採購 / 到貨」可言 ——
            所以那一類也不渲染到貨日期。

            兩格包在同一個 grid 格子裡,單頭仍然是既有的四格(類別 / 日期 → 廠商 /
            代管客戶),沒有另開一條格線。 */}
        <div className={`grid gap-4${isFreight ? '' : ' sm:grid-cols-2'}`}>
          <TextField
            id="po-txn-date"
            label={isFreight ? '帳單日期 *' : '採購日期 *'}
            type="date"
            value={txnDate}
            onChange={setTxnDate}
          />

          {isFreight ? null : (
            <TextField
              id="po-received-date"
              label="到貨日期"
              type="date"
              value={receivedDate}
              onChange={setReceivedDate}
              hint="實際到貨入庫日,可到貨後再回來補"
            />
          )}
        </div>

        {isFreight ? (
          <TextField
            id="po-statement-no"
            label="對帳單號"
            value={statementNo}
            onChange={setStatementNo}
            placeholder="廠商月結單號"
          />
        ) : null}

        {/* 廠商卡片固定三行:簡稱+編號 / 電話 / 地址(REQ-20260902008、REQ-20260902012)。
            這裡的 `visibleFields` 現在只負責拿掉統編 —— 稅額那一格已於 REQ-20260903001
            從 `PartyPicker` 整個刪除,其他呼叫點也都看不到了。
            建單時廠商主檔的稅別**仍然**決定下面合計旁的稅別預設(見 `handleSupplierChange`),
            那讀的是 party 物件,與卡片顯不顯示無關。
            標題用 `labelText` 蓋成「廠商 *」(REQ-20260903003 起就是這個做法,
            REQ-20260905006 只換了文字)。**只有這一個呼叫點改** ——
            改 `PartyPicker` 對 supplier 的預設標題會連應付對帳一起變(那裡沒傳 label),
            而同一張表單下面的「用紙廠商(選填)」要靠標題區分兩張廠商卡片,也不能跟著改 */}
        <div className="grid gap-1">
          <PartyPicker
            type="supplier"
            value={supplier}
            onChange={handleSupplierChange}
            labelText="廠商 *"
            disabled={isEdit}
            visibleFields={['phone', 'address']}
          />
          {/* 運費類在廠商卡片底下多一行付款條件(REQ-20260907002 附圖)。
              ⚠️ **查不到就整行不顯示**,不造假資料 —— 附圖上那句「上次對帳
              2026/08/05」系統目前沒有任何來源可以算,所以一個字都沒印。
              月結天數是主檔真的有的欄位(`suppliers.paymentDays`),沒設定也留白。 */}
          {isFreight && supplier ? (
            <p className="text-[11px] text-slate-400" data-testid="po-freight-supplier-terms">
              {supplier.paymentDays != null
                ? `月結 ${supplier.paymentDays} 天`
                : supplier.closingDay != null
                  ? `月結帳日 ${supplier.closingDay} 日`
                  : '廠商主檔未設定付款條件'}
            </p>
          ) : null}
        </div>

        {/* 代管客戶只在非運費類顯示:運費是費用不是貨,沒有「代誰保管」這回事,
            而「這趟送給誰」是逐列的事(明細的送達客戶)。 */}
        {!isFreight ? (
        <div className="grid gap-1">
          {/* 代管客戶只是「這批貨代誰保管」,不是付款對象 —— 統編 / 地址 / 結帳週期
              那些會計核對欄位在這裡用不到,只留名稱 / 編號 / 電話(REQ-20260819001)。
              標題的「(選填)」自 REQ-20260905006 起改成標題右邊一個灰色小標:
              必填的三格帶 `*`、選填的帶「選填」,兩種標示看同一排就分得出來。
              `label` 仍然傳「代管客戶」—— 它同時是 placeholder 與「找不到符合的…」
              訊息用的稱呼,只有顯示出來的標題走 `labelText`。 */}
          <PartyPicker
            type="customer"
            value={customer}
            onChange={setCustomer}
            label="代管客戶"
            labelText={
              <>
                代管客戶 <span className="text-[11px] text-slate-400">選填</span>
              </>
            }
            visibleFields={['phone']}
          />
          <p className="text-[11px] text-slate-400">
            填了代管客戶,確認時系統會比對這個客戶的累計出貨量,
            「進貨 &gt; 出貨」的部分自動拋轉成代管庫存。
          </p>
        </div>
        ) : null}
      </div>

      {/*
        付款方式(REQ-20260907002)。**只有運費類有這張卡**。

        ⚠️ 與 REQ-20260813003 被使用者指名刪掉的那個「入庫與付款」fieldset
        **不是同一件事**,所以這不是把它叫回來:
         - 那一格(`paymentMethod`)問的是「用什麼工具付」(現金 / 匯款 / 支票 /
           月結),對所有進貨單都成立,而且它的值改由後端依廠商付款條件推算。
         - 這一格(`freightPaymentMode`)問的是「這筆運費要不要變成應付」——
           到付根本不是我方的錢。它**改變金額歸屬**,而且只在運費類出現。
        `paymentMethod` 那一格仍然沒有 UI、仍然走後端推算,一個字都沒動。
      */}
      {isFreight ? (
        <fieldset className="grid gap-2 border rounded-md p-3" data-testid="po-freight-payment">
          <div className="flex items-baseline justify-between gap-2">
            <legend className="text-sm font-medium text-slate-700">付款方式 *</legend>
            <span className="text-[11px] text-slate-400">決定進貨應付帳款還是直接沖現金</span>
          </div>
          {/* 四選一。**不是 `<select>`** —— 四個選項各自代表一種帳務走向,
              攤開來比藏在下拉裡看得清楚(附圖也是四顆鈕) */}
          <div className="flex flex-wrap gap-2">
            {(Object.keys(FREIGHT_PAYMENT_MODES) as FreightPaymentMode[]).map((mode) => (
              <button
                key={mode}
                type="button"
                data-testid={`po-freight-mode-${mode}`}
                aria-pressed={freightPaymentMode === mode}
                onClick={() => setFreightPaymentMode(mode)}
                className={`rounded border px-3 py-1.5 text-sm ${
                  freightPaymentMode === mode
                    ? 'border-blue-600 bg-blue-600 text-white'
                    : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                {FREIGHT_PAYMENT_MODES[mode]}
              </button>
            ))}
          </div>

          {/* 現付才問來源 / 付款人 / 收據 —— 月結與到付沒有「現在付出去的錢」 */}
          {freightPaymentMode === 'CASH' ? (
            <div className="grid gap-3 md:grid-cols-3 items-start">
              {/* ⚠️ 常用值只是建議不是值域(清單外的來源照樣打得進去),
                  而且**正確的清單待使用者確認** —— 見 REQ-20260907002 的待確認事項 */}
              <MultiSelectField
                label="現付來源 *"
                testId="po-freight-cash-source"
                multiple={false}
                compact
                options={[...FREIGHT_CASH_SOURCE_OPTIONS]}
                selected={cashSource ? [cashSource] : []}
                onChange={(items) => setCashSource(items[0] ?? '')}
              />
              {/* 附圖那句「由現場記錄自動帶入」的資料來源目前不存在,
                  所以先做**自由文字** —— 待確認,見 REQ-20260907002 */}
              <TextField
                id="po-freight-payer"
                label="付款人 *"
                value={payerName}
                onChange={setPayerName}
              />
              <div className="grid gap-1">
                <span className="text-sm text-slate-600">收據憑證</span>
                <span className="justify-self-start">
                  <DocumentReceiptButton
                    docType="PURCHASE_ORDER"
                    docId={editOrder?.id}
                    category="SIGNED_RECEIPT"
                    label="收據憑證"
                    testId="po-freight-receipts"
                  />
                </span>
              </div>
            </div>
          ) : null}
        </fieldset>
      ) : null}

      {/*
        派工單的單頭欄位(REQ-20260818002)。紙本派工單上「代工單位」與「用紙廠商」
        是兩家不同的廠商,交件日期與取件方式則是給代工廠的交付指示。
        非派工類的進貨單維持原本的精簡表單 —— 這幾格不顯示,值也不會被送出。
      */}
      {isWorkOrder ? (
        <fieldset className="grid gap-3 border rounded-md p-3" data-testid="po-work-order-header">
          <legend className="text-sm font-medium text-slate-700">派工資訊</legend>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="grid gap-1">
              <PartyPicker
                type="supplier"
                value={paperSupplier}
                onChange={setPaperSupplier}
                label="用紙廠商(選填)"
                visibleFields={['phone', 'address']}
              />
              <p className="text-[11px] text-slate-400">
                紙由這家供、工由上面的代工單位做。
                <strong>金額仍然全數掛在代工單位名下</strong>,不會另外產生一筆應付;
                兩家要各自結帳請開兩張單。
              </p>
            </div>

            <TextField
              id="po-due-date"
              label="交件日期"
              type="date"
              value={dueDate}
              onChange={setDueDate}
            />

            <TextField
              id="po-pickup-method"
              label="取件方式"
              value={pickupMethod}
              onChange={setPickupMethod}
              placeholder="例 完成通知自取 / 送至某某單位"
            />
          </div>
        </fieldset>
      ) : null}

      {/*
        運費明細(REQ-20260907002 附圖第三張卡)。**沿用既有的 `purchase_order_lines`**,
        不另開一張運費明細表:運費在會計上就是「一列一筆金額」,金額 / 稅別 / 歸屬工單
        全部沿用既有欄位與既有算式。開第二張表等於讓「一張單的金額從哪裡加總」
        有兩個答案。

        ⚠️ REQ-20260908002 新增的 `freight_lines` **不是這句話的反例** ——
        它是「歸單之前」的暫存事實(出貨端寫入時**還沒有任何進貨單**可以掛),
        歸單時每一筆仍然轉成一列 `purchase_order_lines`,金額仍然只由
        `calcPurchaseTotals()` 加總。分界是**時間**不是內容,詳見 freight-line.schema.ts。

        欄位對應:運送日 = `shipDate`、託運單號 = `trackingNo`、
        送達客戶 = `deliveryCustomerId`、付款 = `linePaymentType`、
        憑證 = `document_attachments`(docType='PURCHASE_ORDER_LINE')、
        金額 = 既有的 `qty`(固定 1)× `unitPrice`、歸屬工單 = 既有的複選 join table。

        版面沿用 REQ-20260903002 立下的規則:同一條 `md:grid-cols-12` 格線 +
        每格 `md:col-span-*` + `items-start`(挑選框展開時只有那一格長高)。
      */}
      {isFreight ? (
        <fieldset className="grid gap-2" data-testid="po-freight-lines">
          <div className="flex items-baseline justify-between gap-2">
            <legend className="text-sm font-medium text-slate-700">運費明細</legend>
            <span className="text-[11px] text-slate-400">共 {lines.length} 筆</span>
          </div>

          {lines.map((line, i) => {
            const effective = effectiveLinePayment(line, freightPaymentMode);
            const isCollect = effective === 'COLLECT';
            const scan = trackingScan[i] ?? IDLE_SCAN;
            return (
              <div key={i} className="grid items-start gap-2 border rounded-md p-3 md:grid-cols-12">
                {/* 12 欄格線的分配:運送日 2 / 託運單號 2 / 送達客戶 2 / 付款 1 /
                    憑證 1 / 金額 2 / 歸屬工單 2 = 12。`TextField` 自己沒有
                    `className`(它不是品項那顆 `Field`),所以包一層 div 掛欄寬 ——
                    不為了這一格去改 `TextField` 的簽名。
                    ⚠️ 實際欄寬要在**真的瀏覽器**上量(jsdom 沒有版面):
                    input 的自動最小寬約 172px,窄格要記得 `min-w-0` */}
                <div className="md:col-span-2 min-w-0">
                  <TextField
                    id={`po-freight-ship-date-${i}`}
                    label="運送日"
                    type="date"
                    value={line.shipDate}
                    onChange={(v) => setLine(i, { shipDate: v })}
                  />
                </div>
                {/* 託運單號 + 掃描入口(REQ-20260907003)。
                    掃描鈕**放在欄位下方**、`text-[11px]`,不在 12 欄格線裡再切一格 ——
                    這一列已經很擠(REQ-20260907002「沒有在瀏覽器上量過的一件事」)。
                    鈕在 `Field` 的**外面**是刻意的:`Field` 是一個包住 input 的
                    `<label>`,而 `<button>` 是 labelable element,包進去會讓
                    `getByLabelText('託運單號')` 一次抓到兩個元素。 */}
                <div className="md:col-span-2 min-w-0 grid gap-0.5">
                  <Field
                    label="託運單號"
                    value={line.trackingNo}
                    onChange={(v) => setLine(i, { trackingNo: v })}
                  />
                  <button
                    type="button"
                    data-testid={`po-freight-scan-tracking-${i}`}
                    disabled={scan.scanning}
                    onClick={() => scanInputs.current[i]?.click()}
                    title="拍一張托運單照片自動讀出單號。這張圖只用來辨識,不會上傳保存;要留存單據請用本列的「憑證」或表單下方的單據附件。"
                    className="justify-self-start text-[11px] text-blue-600 hover:underline disabled:text-slate-400 disabled:no-underline"
                  >
                    {scan.scanning ? '辨識中…' : '掃描單號'}
                  </button>
                  {/* 藏起來的檔案輸入,由上面的鈕觸發。`capture` 讓手機直接開相機,
                      桌機瀏覽器會忽略它、退化成一般選檔(同 DocumentAttachmentCard) */}
                  <input
                    ref={(el) => {
                      scanInputs.current[i] = el;
                    }}
                    type="file"
                    accept={TRACKING_NO_SCAN_ACCEPT}
                    capture="environment"
                    className="hidden"
                    data-testid={`po-freight-scan-input-${i}`}
                    onChange={(e) => void onScanTrackingNo(i, e.target.files?.[0])}
                  />
                  {scan.filled ? (
                    <span className="text-[11px] text-slate-400">已由圖片帶入,請核對</span>
                  ) : null}
                  {scan.candidates.length > 0 ? (
                    <div
                      className="flex flex-wrap items-center gap-1"
                      data-testid={`po-freight-scan-candidates-${i}`}
                    >
                      <span className="text-[11px] text-slate-500">讀到多組,請選:</span>
                      {scan.candidates.map((c) => (
                        <button
                          key={c.trackingNo}
                          type="button"
                          onClick={() => onPickScanCandidate(i, c.trackingNo)}
                          className="rounded border px-1.5 py-0.5 text-[11px] text-slate-700 hover:bg-slate-50"
                        >
                          {c.carrier ? `${c.trackingNo} · ${c.carrier}` : c.trackingNo}
                        </button>
                      ))}
                    </div>
                  ) : null}
                  {scan.error ? (
                    <span
                      className="text-[11px] text-red-600"
                      data-testid={`po-freight-scan-error-${i}`}
                    >
                      {scan.error}
                    </span>
                  ) : null}
                </div>
                {/* 送達客戶用**既有的客戶挑選器**(不自己做一個)。每一列一顆,
                    所以要傳 `inputId` —— 不傳的話多顆共用同一個 id(見 PartyPicker) */}
                <div className="md:col-span-2 min-w-0">
                  <PartyPicker
                    type="customer"
                    value={line.deliveryCustomer}
                    onChange={(party) => setLine(i, { deliveryCustomer: party })}
                    label="送達客戶"
                    labelText="送達客戶"
                    inputId={`po-freight-delivery-customer-${i}`}
                    visibleFields={[]}
                  />
                </div>
                {/* 單頭選「逐筆指定」時才逐列可改;其餘三種時這一格是唯讀的
                    ——「跟單頭走」在畫面上要看得出來,不是留白 */}
                <div className="md:col-span-1 min-w-0 grid gap-0.5">
                  <span className="text-xs text-slate-600">付款</span>
                  {freightPaymentMode === 'PER_LINE' ? (
                    <div className="flex flex-wrap gap-1">
                      {(
                        Object.keys(PURCHASE_LINE_PAYMENT_TYPES) as PurchaseLinePaymentType[]
                      ).map((type) => (
                        <button
                          key={type}
                          type="button"
                          data-testid={`po-freight-line-payment-${i}-${type}`}
                          aria-pressed={effective === type}
                          onClick={() => setLine(i, { linePaymentType: type })}
                          className={`rounded border px-1.5 py-0.5 text-[11px] ${
                            effective === type
                              ? 'border-blue-600 bg-blue-50 text-blue-700'
                              : 'text-slate-600 hover:bg-slate-50'
                          }`}
                        >
                          {PURCHASE_LINE_PAYMENT_TYPES[type]}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <span className="py-1.5 text-sm text-slate-500">
                      {effective ? PURCHASE_LINE_PAYMENT_TYPES[effective] : '—'}
                    </span>
                  )}
                </div>
                {/* 憑證是**另一顆輕量元件**(需求原文:不要與單據附件卡片混在一起)。
                    建單模式下明細列還沒有 id,鈕停用 —— 不為它改建單流程 */}
                <div className="grid gap-0.5">
                  <span className="text-xs text-slate-600">憑證</span>
                  <span className="justify-self-start py-1">
                    <DocumentReceiptButton
                      docType="PURCHASE_ORDER_LINE"
                      docId={line.id ?? undefined}
                      testId={`po-freight-line-receipt-${i}`}
                    />
                  </span>
                </div>
                {/* 金額走既有的 `qty × unitPrice`(數量固定 1,見送出時的 payload)。
                    到付的列畫上刪除線 —— 它不列入本公司成本與應付 */}
                <div className="md:col-span-2 min-w-0 grid gap-0.5">
                  <Field
                    label="金額"
                    value={line.unitPrice}
                    onChange={(v) => setLinePricing(i, { unitPrice: v })}
                  />
                  {isCollect ? (
                    <span className="text-[11px] text-slate-400 line-through">
                      {withThousands(line.unitPrice || '0')}
                    </span>
                  ) : null}
                </div>
                {/* 來源(REQ-20260908002)。⚠️ **出貨沒有自己的單號** ——
                    顯示的是它所屬那張訂單的 B 字軌單號,點擊回查那張單。
                    手動補登的列沒有來源 */}
                <div className="md:col-span-2 min-w-0 grid gap-0.5">
                  <span className="text-xs text-slate-600">來源</span>
                  {line.freightLineId != null && line.freightSourceOrderNo ? (
                    <a
                      href={`/accounting?tab=sales&editOrderId=${line.freightSourceOrderId ?? ''}`}
                      data-testid={`po-freight-source-${i}`}
                      className="py-1.5 text-sm font-mono text-blue-600 hover:underline truncate"
                    >
                      {line.freightSourceOrderNo}
                    </a>
                  ) : (
                    <span
                      className="py-1.5 text-sm text-slate-400"
                      data-testid={`po-freight-source-${i}`}
                    >
                      手動補登
                    </span>
                  )}
                </div>

                {/*
                  歸屬 / 去向(REQ-20260908002,原本叫「歸屬工單」)。
                  **獨佔一整列** —— 原本 12 欄已經被運送日 / 託運單號 / 送達客戶 /
                  付款 / 憑證 / 金額 / 來源填滿,分攤面板也需要整列的寬度才展得開。
                  ⚠️ jsdom 量不出實際欄寬,這一列的排版需要在真的瀏覽器上再看一次。

                  四種狀態:工單號(正常)/ 待分攤 · N 張工單 / 待指定去向 / 不計成本(到付)。
                */}
                <div className="md:col-span-12 grid gap-1 border-t pt-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-slate-600">歸屬 / 去向</span>
                    {isCollect ? (
                      <span
                        className="text-sm text-slate-400"
                        data-testid={`po-freight-destination-${i}`}
                      >
                        不計成本
                      </span>
                    ) : line.workOrderNos.length >= 2 ? (
                      <button
                        type="button"
                        data-testid={`po-freight-destination-${i}`}
                        aria-expanded={expandedLine === i}
                        onClick={() => setExpandedLine(expandedLine === i ? null : i)}
                        className="rounded border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] text-amber-800 hover:bg-amber-100"
                      >
                        待分攤 · {line.workOrderNos.length} 張工單
                      </button>
                    ) : line.workOrderNos.length === 0 ? (
                      <button
                        type="button"
                        data-testid={`po-freight-destination-${i}`}
                        aria-expanded={expandedLine === i}
                        onClick={() => setExpandedLine(expandedLine === i ? null : i)}
                        className="rounded border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] text-amber-800 hover:bg-amber-100"
                      >
                        {costAllocationMode === 'OPERATING_EXPENSE' ? '計入營業費用' : '待指定去向'}
                      </button>
                    ) : (
                      <span
                        className="font-mono text-sm text-slate-700"
                        data-testid={`po-freight-destination-${i}`}
                      >
                        {line.workOrderNos[0]}
                      </span>
                    )}
                  </div>

                  {/* 到付那一支維持既有行為:**不渲染挑選框**(REQ-20260907002) */}
                  {!isCollect ? (
                    <FreightWorkOrderPicker
                      customerId={line.deliveryCustomer?.id}
                      testId={`po-freight-work-order-no-${i}`}
                      value={line.workOrderNos}
                      onChange={(nos) => setLine(i, { workOrderNos: nos })}
                    />
                  ) : null}

                  {/* 就地展開,不跳畫面(需求原文)。淡黃底同附圖 */}
                  {!isCollect && expandedLine === i && line.workOrderNos.length >= 2 ? (
                    <FreightAllocationPanel
                      testId={`po-freight-allocation-${i}`}
                      amount={line.unitPrice}
                      customerId={line.deliveryCustomer?.id}
                      workOrderNos={line.workOrderNos}
                      method={line.allocationMethod}
                      amounts={line.workOrderAmounts}
                      onChange={(next) => setLine(i, next)}
                    />
                  ) : null}

                  {!isCollect && expandedLine === i && line.workOrderNos.length === 0 ? (
                    <div
                      className="rounded border border-amber-200 bg-amber-50 px-3 py-2 grid gap-1"
                      data-testid={`po-freight-destination-panel-${i}`}
                    >
                      <p className="text-[11px] text-amber-800">
                        這一趟沒有對應的工單。指定工單就在上面的挑選框挑;
                        沒有工單可歸的話,整張單走「計入營業費用」。
                      </p>
                      <button
                        type="button"
                        data-testid={`po-freight-operating-expense-${i}`}
                        onClick={() => setCostAllocationMode('OPERATING_EXPENSE')}
                        className="justify-self-start rounded border px-2 py-0.5 text-[11px] text-slate-700 hover:bg-white"
                      >
                        計入營業費用
                      </button>
                      {/* ⚠️ 「計入營業費用」是**單頭**的成本歸屬(`costAllocationMode`,
                          REQ-20260907002 已有的欄位)—— 明細沒有這個維度的欄位,
                          為一列的去向多開一欄會讓「這張單的成本去向」有兩個答案 */}
                      <span className="text-[11px] text-slate-500">
                        這會把整張單的「成本歸屬」設成計入營業費用。
                      </span>
                    </div>
                  ) : null}
                </div>

                {lines.length > 1 ? (
                  <div className="md:col-span-12 text-right">
                    <button
                      type="button"
                      onClick={() => setLines((prev) => prev.filter((_, idx) => idx !== i))}
                      className="text-red-600 hover:underline text-xs"
                    >
                      刪除此列
                    </button>
                  </div>
                ) : null}
              </div>
            );
          })}

          <button
            type="button"
            onClick={() => setLines((prev) => [...prev, { ...EMPTY_LINE }])}
            className="justify-self-start text-blue-600 hover:underline text-sm"
          >
            + 新增一筆
          </button>
          <p className="text-[11px] text-slate-400">
            到付由收貨方支付,金額僅供記錄,不列入本公司成本與應付。
          </p>
          {freightPending.length > 0 ? (
            <p className="text-[11px] text-amber-700" data-testid="po-freight-pending">
              待補:{freightPending.join('、')}
            </p>
          ) : null}
        </fieldset>
      ) : null}

      {!isFreight ? (
      <fieldset className="grid gap-2">
        {/* legend 要與右邊那行小字併成一列就得包一層 div —— `<legend>` 是 fieldset 的
            直屬子節點時排不進 flex(瀏覽器把它當成框線缺口上的標題另外排),
            做法與 `MultiSelectField` 精簡版同一套(REQ-20260905006) */}
        <div className="flex items-baseline justify-between gap-2">
          <legend className="text-sm font-medium text-slate-700">品項</legend>
          <span className="text-[11px] text-slate-400">帶 * 為必填</span>
        </div>
        {lines.map((line, i) => (
          <div key={i} className="grid gap-2 border rounded-md p-3">
            {/*
              品項第一列依類別分兩種版面(REQ-20260821006)。外包加工單填的是
              **一項加工服務**而不是一批紙:品名其實是加工項目、規格講的是加工規格,
              而且單位要跟在數量後面 ——「先填單位再填數量」是紙張採購的順序,
              發包加工時填起來卡。其餘類別(含派工)**一格都沒動**。
            */}
            {isOutsourceProcessing ? (
              // `items-start`:加工項目展開時那一格會長高,不加的話同列其他五格會
              // 跟著被拉成一個個大方框(REQ-20260821007,在瀏覽器看到才發現)
              <div
                className="grid items-start gap-2 md:grid-cols-6"
                data-testid={`po-line-outsource-${i}`}
              >
                {/* 這一格填的其實是**加工項目**,所以標籤就叫加工項目(REQ-20260821007);
                    存的欄位仍然是 `productName`,zod / API / DB 一個字都沒動。
                    清單吃「設定 → 印刷加工」的既有 reference(processing_options,與下面那格
                    加工同一份,不多打一支 API),挑選框與下面那格同一顆元件、同一種操作 ——
                    但**只挑一個**:一項加工一列,要記兩項就按「新增品項」。
                    仍然**只是建議不是值域**:清單外的字在下面的輸入框照樣打得進去。 */}
                <MultiSelectField
                  label="加工項目"
                  testId={`po-line-product-name-${i}`}
                  multiple={false}
                  options={optionNames(processingOpts?.data)}
                  selected={line.productName ? [line.productName] : []}
                  onChange={(items) => setLine(i, { productName: items[0] ?? '' })}
                />
                <Field label="加工規格" value={line.productSpec} onChange={(v) => setLine(i, { productSpec: v })} />
                <Field label="數量" value={line.qty} onChange={(v) => setLinePricing(i, { qty: v })} />
                {/* ⚠️ 這個版面**刻意一格都沒接**鍵盤跳格與單位提示(REQ-20260911001):
                    需求附圖與原文講的是商品採購那一支,而這一列的欄位順序是
                    REQ-20260821006 為發包加工另外排的;更直接的是 REQ-20260821006 /
                    REQ-20260904003 各留了一條「外包加工類的第一列一格都沒被改到」
                    在釘這六格,連 `textContent` 都比對過。單位沒填仍然會被送出時的
                    提醒視窗攔到,Enter 不送出表單也照樣吃得到(守門在 `<form>` 上)。 */}
                <Field label="單位" value={line.unitName} onChange={(v) => setLine(i, { unitName: v })} />
                {/* 單價提示(REQ-20260917001)包一層 div 才會落在這一格底下 ——
                    直接當格線的第七個子節點會換行跑到下一列的最左邊 */}
                <div className="grid gap-0.5">
                  <Field label="單價" value={line.unitPrice} onChange={(v) => setLinePricing(i, { unitPrice: v })} />
                  {isPriceMissing(line.unitPrice) ? <PriceMissingHint index={i} /> : null}
                </div>
                <Field
                  label="總價(數量 × 單價)"
                  value={line.amount}
                  onChange={(v) => setLineAmount(i, v)}
                />
              </div>
            ) : isGoodsPurchase ? (
              // 「品項與金額」的**商品採購版**(REQ-20260904003)。進的是成品不是紙,
              // 所以精簡成八格:品名 / 規格描述 / 材質描述 / 加工描述(第一列)、
              // 數量 / 單位 / 單價 / 總價(第二列)。下面的「紙張規格」整組不渲染。
              //
              // 沿用 REQ-20260903002 立下的規則:小標題 + 同一條 `md:grid-cols-12` 格線 +
              // 每格都掛 `md:col-span-*`,不另開一條自己的 `md:grid-cols-N`。
              // 八格一律 3 欄:兩列的分欄點都落在第 3 / 6 / 9 欄之後,直向對得齊,
              // 而且八格都是單值輸入框(唯一的挑選框是單位),不需要誰特別寬。
              // `items-start`:單位那格展開選項時會長高,不加的話同列其他三格會被拉長
              // (與既有兩支分支踩過的是同一個坑,REQ-20260821007)
              <div className="grid gap-1" data-testid={`po-line-group-item-${i}`}>
                <p className="text-[11px] text-slate-500">品項與金額</p>
                <div className="grid items-start gap-2 md:grid-cols-12">
                  {/* 品名。**沒有**一般版面那句「例:特級雙面銅西紙」的提示 ——
                      這一支不填紙,拿紙的例子當提示只會誤導 */}
                  <Field
                    className="md:col-span-3"
                    label="品名"
                    value={line.productName}
                    onChange={(v) => setLine(i, { productName: v })}
                  />
                  {/* 規格描述 / 材質描述 / 加工描述三格都是**單一輸入框**(需求原文指名)——
                      商品是現成的成品,規格與加工是一句描述而不是可挑選的值域。
                      規格描述沿用 `productSpec`(一般版面在那一格放的是大紙規格挑選框),
                      加工描述沿用 `postProcessing`;只有材質描述是本次新增的欄位。 */}
                  <Field
                    className="md:col-span-3"
                    label="規格描述"
                    value={line.productSpec}
                    onChange={(v) => setLine(i, { productSpec: v })}
                  />
                  <Field
                    className="md:col-span-3"
                    label="材質描述"
                    value={line.productMaterial}
                    onChange={(v) => setLine(i, { productMaterial: v })}
                  />
                  {/* ⚠️ 這一格與 `showsProcessingFields()` 那組的「加工 / 後製加工」是
                      **同一個欄位**、不同版面的兩個入口。商品採購不含派工 / 外包 / 加工,
                      那一組本來就不渲染,所以兩格不會同時出現 —— 也**不要**為了這一格
                      去放寬 `showsProcessingFields()`,那會讓商品採購單冒出兩格加工。 */}
                  <Field
                    className="md:col-span-3"
                    label="加工描述"
                    value={line.postProcessing}
                    onChange={(v) => setLine(i, { postProcessing: v })}
                  />
                  {/* 數量 → 單位 → 單價 → 總價的鍵盤跳格(REQ-20260911001)。
                      Enter 與 → 往右、← 往左,規則見 `focusNextOnKey` / `focusPrevOnKey`。
                      **總價的 Enter 不再往下一列跳、也不送出**:需求只講到「往下一格」,
                      跨列要先回答「下一列還沒建出來怎麼辦」,猜錯的成本比留白高。 */}
                  <Field
                    className="md:col-span-3"
                    label="數量"
                    value={line.qty}
                    onChange={(v) => setLinePricing(i, { qty: v })}
                    inputRef={(el) => {
                      qtyInputs.current[i] = el;
                    }}
                    onKeyDown={(e) =>
                      focusNextOnKey(
                        e,
                        ['Enter', 'ArrowDown', 'ArrowRight'],
                        () => unitInputs.current[i],
                      )
                    }
                  />
                  {/* 單位:需求要「下拉選單」,但**不能用封閉的 `<select>`**
                      (REQ-20260902003 §2.2 / REQ-20260903002 §2.3 已經否決過)——
                      正式資料有清單外的舊單位值,`<select>` 對不到 option 會顯示空白,
                      使用者一存檔就把舊值靜默清掉。挑選框點一下就展開完整清單,
                      清單外的值原樣顯示、也從下面的輸入框打得進去。

                      ⚠️ `onChange` 走 `setLine` 而**不是** `setLineUnitName()`:
                      後者選到「令」時會把換算率自動代成 500,而商品採購把換算率整格
                      藏起來了 —— 使用者看不到卻被偷偷乘 500,庫存單位數量會直接錯掉。
                      外包加工分支也藏了換算率,處理方式相同。

                      單位用精簡版挑選框(一行高,與數量 / 單價對齊,REQ-20260905002):
                      正式環境的貨品單位有二十幾筆,一般版面即使收合仍是
                      legend + 收合鈕 + chip + 輸入框四行,夾在兩格單值 `Field` 中間高一大截。 */}
                  <div className="md:col-span-3">
                    <MultiSelectField
                      label="單位"
                      testId={`po-line-unit-name-${i}`}
                      multiple={false}
                      compact
                      options={optionNames(unitOpts?.data)}
                      selected={line.unitName ? [line.unitName] : []}
                      onChange={(items) => setLine(i, { unitName: items[0] ?? '' })}
                      // 跳格接的是挑選框裡那個**手動輸入框**(REQ-20260911001)——
                      // 元件自己的 Enter 會先 `commitDraft()` 把打的字收進去,才輪到這裡
                      inputRef={(el) => {
                        unitInputs.current[i] = el;
                      }}
                      onInputKeyDown={(e) => {
                        focusNextOnKey(e, ['Enter', 'ArrowRight'], () => unitPriceInputs.current[i]);
                        focusPrevOnKey(e, ['ArrowLeft'], () => qtyInputs.current[i]);
                      }}
                    />
                    {line.unitName ? null : <UnitMissingHint index={i} />}
                  </div>
                  {/* `md:col-span-*` 掛在外層 div 上(同上面的單位那一格):提示是
                      這一格的第二行,不是格線的另一格 */}
                  <div className="md:col-span-3">
                    <Field
                      label="單價"
                      value={line.unitPrice}
                      onChange={(v) => setLinePricing(i, { unitPrice: v })}
                      inputRef={(el) => {
                        unitPriceInputs.current[i] = el;
                      }}
                      onKeyDown={(e) => {
                        focusNextOnKey(e, ['Enter', 'ArrowRight'], () => amountInputs.current[i]);
                        focusPrevOnKey(e, ['ArrowLeft'], () => unitInputs.current[i]);
                      }}
                    />
                    {isPriceMissing(line.unitPrice) ? <PriceMissingHint index={i} /> : null}
                  </div>
                  {/* 需求第 3 點「單價與總價擇一 KEY」**已經是既有行為**
                      (REQ-20260821005 的 `setLinePricing` / `setLineAmount` 雙向連動),
                      這裡只是把同兩支 handler 接上新版面,沒有新邏輯 */}
                  <Field
                    className="md:col-span-3"
                    label="總價(數量 × 單價)"
                    value={line.amount}
                    onChange={(v) => setLineAmount(i, v)}
                    inputRef={(el) => {
                      amountInputs.current[i] = el;
                    }}
                    onKeyDown={(e) =>
                      focusPrevOnKey(e, ['ArrowLeft'], () => unitPriceInputs.current[i])
                    }
                  />
                </div>
              </div>
            ) : (
              // 一般品項(紙張材料 / 派工類共用的那一支)。REQ-20260905006 依使用者附圖
              // 重排成四組:**品名(單獨一列)/ 紙張規格 / 數量與換算 / 金額**,
              // 加上共用的「成本歸屬」共五段。改動前是「品項與金額」+「紙張規格」兩組,
              // 單位各自獨立成一格;現在單位貼進數字框後面(見 `NumberUnitField`)。
              //
              // 四組沿用 REQ-20260903002 立下的規則:同一條 `md:grid-cols-12` 格線 +
              // 每格都掛 `md:col-span-*`,不另開一條自己的 `md:grid-cols-N`。
              // `items-start` 全部保留:挑選框展開時那一格會長高,不加的話同列其他格
              // 會跟著被拉長(REQ-20260821007 在瀏覽器上才發現的坑)。
              //
              // ⚠️ 這一支與下面「紙張規格 / 數量與換算 / 金額」的顯示條件本來就相同
              // (`!isOutsourceProcessing && !isGoodsPurchase`),所以四組併在同一個
              // 分支裡 —— 改動前紙張規格是獨立的一段條件式,兩處要一起改才不會分岔。
              <>
                {/* 品名單獨一列、加寬到 8 欄(圖二)。提示文字(REQ-20260824001)保留 */}
                <div className="grid gap-1" data-testid={`po-line-group-item-${i}`}>
                  <div className="grid items-start gap-2 md:grid-cols-12">
                    <Field
                      className="md:col-span-8"
                      label="品名 *"
                      placeholder="例:特級雙面銅西紙"
                      value={line.productName}
                      onChange={(v) => setLine(i, { productName: v })}
                    />
                  </div>
                </div>

                {/* 「紙張規格」。外包加工 / 商品採購看不到這一整組 —— 見下面那條
                    `isOutsourceProcessing || isGoodsPurchase` 的分支條件說明。 */}
                <div className="grid gap-1 border-t pt-2" data-testid={`po-line-group-paper-${i}`}>
                  <p className="text-[11px] text-slate-500">紙張規格</p>
                  <div className="grid items-start gap-2 md:grid-cols-12">
                    {/* 大紙規格的常用選項(REQ-20260902003)。REQ-20260905006 只改標籤文字
                        (規格 → **原紙規格**,與右邊的「指定交付尺寸」分得出來)與版面
                        (改用 `compact` 精簡版,收合狀態一行高、與同列其他格齊平)——
                        欄位仍是 `productSpec`,schema / API / DB 一個字都沒動。

                        ⚠️ 看起來像下拉,但**不是**封閉的 `<select>`:清單外的規格與舊單的
                        既有值仍然打得進去 / 原樣顯示(REQ-20260902003 §2.2)。

                        挑選框那幾格的 `md:col-span-*` 掛在外面的 `div` 上,不是加進
                        `MultiSelectField` 的 props —— 同 `SalesOrderForm` 既有的寫法。 */}
                    <div className="md:col-span-4">
                      <MultiSelectField
                        label="原紙規格"
                        testId={`po-line-product-spec-${i}`}
                        multiple={false}
                        compact
                        options={PAPER_SPEC_OPTIONS}
                        selected={line.productSpec ? [line.productSpec] : []}
                        onChange={(items) => setLine(i, { productSpec: items[0] ?? '' })}
                      />
                    </div>
                    {/* 裁修完成交付的開數。REQ-20260905006 把標籤由「尺寸 / 開數」改成
                        **指定交付尺寸**(欄位仍是 `sizeSpec`),版面改用 `compact`。
                        ⚠️ **不能用封閉的 `<select>`**:正式資料裡有「菊全 / 四六全 /
                        菊八開 / 四六八開」這些不在清單裡的舊值,`<select>` 遇到清單外的值
                        會顯示空白,使用者一存檔就把舊值靜默清掉 —— 而 `sizeSpec` 同時是
                        派工單列印頁 `WorkOrderSheet` 印出來的「紙張尺寸」。 */}
                    <div className="md:col-span-4">
                      <MultiSelectField
                        label="指定交付尺寸"
                        testId={`po-line-size-spec-${i}`}
                        multiple={false}
                        compact
                        options={[...PURCHASE_SIZE_SPECS]}
                        selected={line.sizeSpec ? [line.sizeSpec] : []}
                        onChange={(items) => setLine(i, { sizeSpec: items[0] ?? '' })}
                      />
                    </div>
                    {/* 「每張刀裁」(REQ-20260905006 圖二新增)。**純顯示、不進 payload**,
                        值由左邊的「指定交付尺寸」推得 —— 推法見 `cutsPerSheet()` 上方的
                        註解,那是個**待使用者確認的假設**,所以刻意不讓它碰任何計算。 */}
                    <AutoField
                      className="md:col-span-4"
                      label="每張刀裁"
                      value={cutsPerSheet(line.sizeSpec)}
                    />
                    {/* 紙張顏色(REQ-20260902003)。跟廠商叫紙時「白 / 牛皮」是規格的一部分,
                        原本只能硬塞進品名或規格那兩格。
                        ⚠️ 與下面派工欄位群的「印刷顏色」(`printColor`)是**兩個欄位**:
                        那是印上去的顏色,這是紙本身的顏色,一個字都沒共用。 */}
                    <Field
                      className="md:col-span-4"
                      label="紙張顏色"
                      placeholder="例:白、牛皮、米黃"
                      value={line.paperColor}
                      onChange={(v) => setLine(i, { paperColor: v })}
                    />
                    {/* 厚度 + 單位(REQ-20260902003 加的單位,REQ-20260905006 把它併成
                        輸入框的後綴)。不同紙材的厚度標示法不同(克重 / 條 / 磅 / 盎司 /
                        微米)—— 只留一個數字欄位,看到「120」分不出是 120gsm 還是 120 條。
                        標籤由「厚度 / 磅數」縮成「厚度」(欄位仍是 `weightGsm`);
                        必填星號的判斷式沿用原本的 `orderType === 'PAPER'`,沒有動。
                        ⚠️ 單位走 `setLine` —— 只有**數量單位**才有「令 → 換算率 500」。 */}
                    <NumberUnitField
                      className="md:col-span-4"
                      label={orderType === 'PAPER' ? '厚度 *' : '厚度'}
                      value={line.weightGsm}
                      onChange={(v) => setLine(i, { weightGsm: v })}
                      unitLabel="厚度單位"
                      unitTestId={`po-line-weight-unit-${i}`}
                      unitOptions={WEIGHT_UNIT_OPTIONS}
                      unitValue={line.weightUnit}
                      onUnitChange={(v) => setLine(i, { weightUnit: v })}
                    />
                    <SelectField
                      className="md:col-span-4"
                      label="紋路"
                      value={line.grainDirection}
                      onChange={(v) =>
                        setLine(i, { grainDirection: v as '' | PurchaseGrainDirection })
                      }
                      options={Object.entries(PURCHASE_GRAIN_DIRECTIONS)}
                      placeholder="未指定"
                    />
                  </div>
                </div>

                {/* 「數量與換算」(REQ-20260905006)。改動前數量在「品項與金額」、
                    換算率與庫存單位數量在「紙張規格」,三格分屬兩組看不出關聯。 */}
                <div className="grid gap-1 border-t pt-2" data-testid={`po-line-group-qty-${i}`}>
                  <p className="text-[11px] text-slate-500">數量與換算</p>
                  <div className="grid items-start gap-2 md:grid-cols-12">
                    {/* 數量 + 數量單位。常用單位挑得到、清單外的也打得進去
                        (REQ-20260824001);選到「令」時換算率自動代 500 ——
                        ⚠️ 所以這一格的單位走 `setLineUnitName()` 而**不是** `setLine`。 */}
                    {/* 數量 → 單價 → 總價的鍵盤跳格(REQ-20260911001)。
                        ⚠️ 這個版面的**單位不在跳格鏈上**:它藏在數字框右邊那顆鈕
                        後面,要先按鈕把挑選框掛出來才有東西可以 focus。從數量 Enter
                        直接跳進一個還沒 render 的輸入框做不到,而「Enter 順便幫你
                        把面板打開」等於替使用者決定他要開那個面板 —— 商品採購版的
                        單位是一直在畫面上的精簡挑選框,那一支才接得進來。 */}
                    <NumberUnitField
                      className="md:col-span-4"
                      label="數量 *"
                      value={line.qty}
                      onChange={(v) => setLinePricing(i, { qty: v })}
                      unitLabel="數量單位"
                      unitTestId={`po-line-unit-name-${i}`}
                      unitOptions={UNIT_NAME_OPTIONS}
                      unitValue={line.unitName}
                      onUnitChange={(v) => setLineUnitName(i, v)}
                      inputRef={(el) => {
                        qtyInputs.current[i] = el;
                      }}
                      onKeyDown={(e) =>
                        focusNextOnKey(
                          e,
                          ['Enter', 'ArrowDown', 'ArrowRight'],
                          () => unitPriceInputs.current[i],
                        )
                      }
                      hint={line.unitName ? null : <UnitMissingHint index={i} />}
                    />
                    <Field
                      className="md:col-span-4"
                      label="換算率"
                      alignRight
                      value={line.conversionRate}
                      onChange={(v) => setLine(i, { conversionRate: v })}
                    />
                    <AutoField
                      className="md:col-span-4"
                      label="庫存單位數量"
                      value={withThousands(calcStockUnitQty(line.qty, line.conversionRate || null))}
                    />
                  </div>
                  <p className="text-[11px] text-slate-400">
                    換算率是 1 個數量單位可換成幾個庫存單位;庫存單位數量 = 數量 × 換算率,由系統自動算、不能手填。
                  </p>
                </div>

                {/* 「金額」(REQ-20260905006)。圖二把單價放最左、總價放最右(第 9 欄起),
                    中間那一格刻意留白 —— 兩個金額分得開,不會看錯行。 */}
                <div className="grid gap-1 border-t pt-2" data-testid={`po-line-group-amount-${i}`}>
                  <p className="text-[11px] text-slate-500">金額</p>
                  <div className="grid items-start gap-2 md:grid-cols-12">
                    {/* 標籤**沒有星號**(REQ-20260917001):單價留空也存得起來
                        (送出時補 0),留著星號等於畫面上還宣告它是必填 */}
                    <div className="md:col-span-4">
                      <Field
                        label="單價"
                        alignRight
                        value={line.unitPrice}
                        onChange={(v) => setLinePricing(i, { unitPrice: v })}
                        inputRef={(el) => {
                          unitPriceInputs.current[i] = el;
                        }}
                        onKeyDown={(e) => {
                          focusNextOnKey(e, ['Enter', 'ArrowRight'], () => amountInputs.current[i]);
                          focusPrevOnKey(e, ['ArrowLeft'], () => qtyInputs.current[i]);
                        }}
                      />
                      {isPriceMissing(line.unitPrice) ? <PriceMissingHint index={i} /> : null}
                    </div>
                    {/* 總價與單價雙向連動(REQ-20260821005):會計拿到的憑證有時只寫總價,
                        填哪一邊另一邊都算得出來。送出的仍然只有數量與單價。
                        ⚠️ 圖二把這一格畫成灰底的「自動」欄位,但**仍然保持可編輯** ——
                        使用者在 REQ-20260904003 需求第 3 點才指名「單價與總價可以擇一
                        KEY 入」,改成唯讀等於推翻前一天的明確要求。折衷是位置與分組照圖二,
                        標籤旁標「自動計算」表示不填也會自己算出來。 */}
                    <TaggedField
                      className="md:col-span-4 md:col-start-9"
                      label="總價"
                      tag="自動計算"
                    >
                      <input
                        type="text"
                        aria-label="總價"
                        value={line.amount}
                        ref={(el) => {
                          amountInputs.current[i] = el;
                        }}
                        onChange={(e) => setLineAmount(i, e.target.value)}
                        onKeyDown={(e) =>
                          focusPrevOnKey(e, ['ArrowLeft'], () => unitPriceInputs.current[i])
                        }
                        className="w-full rounded-md border px-2 py-1.5 text-right text-sm"
                      />
                    </TaggedField>
                  </div>
                </div>
              </>
            )}

            {/*
              完成數量(REQ-20260821006)—— 委外加工會耗損:委外 1000 片、送廠 1100 片、
              廠商交回 1060 片,系統原本只記得「委外多少」。派工與外包加工都可能發生,
              所以吃的是 `showsProcessingFields()` 而不是外包那個判斷式。
              旁邊的耗損量**只顯示不存**:它一律是「數量 − 完成數量」算得出來的,
              落地成欄位就會有兩份真相(比照 REQ-20260821005 §四 對總價的態度)。
            */}
            {showsProcessing ? (
              <div className="grid gap-2 md:grid-cols-3" data-testid={`po-line-completed-${i}`}>
                <Field
                  label="完成數量(廠商實際交回)"
                  value={line.completedQty}
                  onChange={(v) => setLine(i, { completedQty: v })}
                />
                <p className="self-end text-[11px] text-slate-500 py-1.5 md:col-span-2">
                  {describeLoss(line.qty, line.completedQty) ??
                    '廠商回報實際完成 / 交回的數量後再填,留空表示尚未回報。'}
                </p>
              </div>
            ) : null}

            {/* 紙張的維度那一組(紙張規格 / 數量與換算 / 金額)自 REQ-20260905006 起
                併進上面那個 `else` 分支 —— 它與一般品項版面的顯示條件本來就相同
                (`!isOutsourceProcessing && !isGoodsPurchase`),分成兩處只會分岔。

                外包加工單不碰紙,那一整組不顯示(REQ-20260821006)—— 但**派工單要留著**:
                「指定交付尺寸」(`sizeSpec`)就是紙本派工單的紙張尺寸,
                `WorkOrderSheet` 列印頁在讀它。
                商品採購類同樣整組不渲染(REQ-20260904003)—— 需求說的是「徹底隱藏」,
                所以是**真的不 render**,不是 CSS `hidden`。
                ⚠️ **不 render ≠ 清空**(REQ-20260821006 §九):`LineDraft` 一律由
                `toLineDraft()` 初始化,送出的 payload 仍然照原樣把 paperColor /
                weightGsm / weightUnit / sizeSpec / grainDirection / conversionRate
                送回去,舊單的值不會被抹掉。代價是**看不到 = 改不動**:
                要改那幾格得先把類別切回紙張類。 */}

            {/* 品項的「稅別(留空 = 沿用單頭)」這一格已移除(REQ-20260901010)——
                使用者看到畫面上有兩格「稅別」判定重複,指名留合計旁那一格。
                `line.taxCategory` 欄位與送出的 payload 都沒動(比照 REQ-20260821008
                對 `postProcessing` 的處理):舊單存過的逐項覆寫值照樣原樣送回去,
                只是沒有入口再改它。要恢復就是把這一格 render 回來。 */}
            {/* 「成本歸屬」(REQ-20260903002)。改動前是 `md:grid-cols-5` 但只放兩格 ——
                後面三欄空著,兩格的左右邊界與上面兩組完全對不上。改成同一條 12 欄格線。
                ⚠️ 這一區**兩種版面共用**(外包加工類也看得到),所以標題與格線是一起變的;
                外包分支要保持原樣的是它的第一列與欄位(REQ-20260821006),不是這一格。 */}
            <div className="grid gap-1 border-t pt-2" data-testid={`po-line-group-cost-${i}`}>
              <p className="text-[11px] text-slate-500">成本歸屬</p>
              <div className="grid items-start gap-2 md:grid-cols-12">
                <SelectField
                  className="md:col-span-3"
                  label="成本去向"
                  value={line.costDestination}
                  onChange={(v) => setLine(i, { costDestination: v as PurchaseCostDestination })}
                  options={Object.entries(PURCHASE_COST_DESTINATIONS)}
                />
                {/* 選了代管客戶就把該客戶近期的應收單號列成建議(REQ-20260819002)。
                    沒選時 `options` 是空陣列,這一格與改動前一樣是純手動輸入。

                    REQ-20260905003:這一格從單值改成**可複選**。一令紙跟廠商叫的價格
                    與拆成兩次各半令不同,所以品項不能為了「這令給哪幾張單用」而拆列 ——
                    改成一列掛多個案號。金額 / 數量 / 庫存一行都沒動,也**不做分攤**。

                    元件從 `DatalistField` 換成 `MultiSelectField`:原生 `<datalist>`
                    只能單選(這是 `MultiSelectField` 檔頭自己寫過的事實),不是換元件
                    就填不進清單外的值 —— 挑選框同樣是「建議不是值域」,手打的案號從
                    下面那格輸入框進得來。`compact` 版面沿用商品採購「單位」那格
                    (REQ-20260905002):收合狀態下一行高,與左邊的「成本去向」對得齊。

                    `md:col-span-*` 掛在外面的 div 上,不是加進 `MultiSelectField`
                    的 props —— 同表單其餘挑選框既有的寫法。 */}
                <div className="md:col-span-5">
                  <MultiSelectField
                    label={line.costDestination === 'DIRECT_TO_WORK_ORDER' ? '工單 / 案號 *' : '工單 / 案號'}
                    testId={`po-line-work-order-no-${i}`}
                    compact
                    options={workOrderNoOptions.map((o) => o.display)}
                    // 顯示的是與清單同一行的「單號 - 品名 等N項」,存的仍然只有純單號
                    // (REQ-20260905001 不可退回)。對不到清單就顯示原值 —— 手打的案號、
                    // 編輯舊單時不在近期 10 筆內的單號都走這條,不會被改寫。
                    selected={line.workOrderNos.map(
                      (no) => workOrderNoOptions.find((o) => o.orderNo === no)?.display ?? no,
                    )}
                    // 反查回純單號;對不到 = 使用者自己打的字,原樣存
                    onChange={(items) =>
                      setLine(i, {
                        workOrderNos: items.map(
                          (v) => workOrderNoOptions.find((o) => o.display === v)?.orderNo ?? v,
                        ),
                      })
                    }
                  />
                </div>
              </div>
            </div>

            {/*
              印刷顏色 / 加工(REQ-20260821005)。派工單以外的**外包加工單**也要填印刷顏色,
              所以判斷式與下面那組派工欄位群分開(見 `showsProcessingFields()`)。

              兩格都是「常用選項打勾 + 可手動輸入」的挑選框 —— REQ-20260818002 決定過
              不做**固定的**加工項目清單(派工單上是「打孔:1 孔孔徑 3mm」這種自由組合句,
              固定清單填不進去),`MultiSelectField` 不是封閉值域:勾選只是常用選項的捷徑,
              清單外的字照樣在下面的輸入框打進去,所以那個決定仍然成立。

              印刷顏色維持**複選**(一件印刷品本來就會有好幾個顏色),**不分類別一律顯示**。
              「加工 / 後製加工」則**只留給非外包加工的類別**(例如純派工,REQ-20260821008):
              外包加工單的第一列已經有「加工項目」,兩格吃同一份 `processing-options`、
              同一顆元件、同一種操作,擺在同一張畫面上就是重複的兩個挑選框。
              仍然是**單選**(REQ-20260821007)—— 一列記一項加工,要記兩項就按「新增品項」
              加一列,金額才有得歸屬。出貨管理那幾格仍然是複選,兩邊刻意不同。
            */}
            {showsProcessing ? (
              <div
                // `items-start` 同第一列:一邊展開選項時,另一邊不該跟著被拉高
                className="grid items-start gap-2 border-t pt-2 md:grid-cols-2"
                data-testid={`po-line-processing-${i}`}
              >
                <MultiSelectField
                  label="印刷顏色"
                  testId={`po-line-print-color-${i}`}
                  options={optionNames(printColorOpts?.data)}
                  selected={splitItems(line.printColor)}
                  onChange={(items) => setLine(i, { printColor: joinItems(items) ?? '' })}
                />
                {/* 外包加工類不渲染這一格(REQ-20260821008)—— 上面的「加工項目」已經是
                    同一份選項、同一種操作,兩格並排使用者只看得到「重複的兩個挑選框」。
                    ⚠️ 只是**不顯示**:`postProcessing` 這個欄位、zod、送出的 payload 一個字
                    都沒動,舊單存的值原封不動跟著 PATCH 回去,不會被清成 null。

                    加工是單選(REQ-20260821007):一張單能用「新增品項」加多列分別記錄
                    不同加工,同一列裡不需要再勾多個。
                    ⚠️ 這裡**不能** `splitItems()` —— 舊單有複選時代存的「上膜、上油」,
                    拆成兩顆 chip 之後單選只留得住一顆,值就被改掉了。整串當一個值顯示,
                    使用者不動它就原封不動送回去(同這張表單對清單外的值一貫的態度 ——
                    挑選框是建議不是值域)。 */}
                {isOutsourceProcessing ? null : (
                  <MultiSelectField
                    label="加工 / 後製加工"
                    testId={`po-line-post-processing-${i}`}
                    multiple={false}
                    options={optionNames(processingOpts?.data)}
                    selected={line.postProcessing ? [line.postProcessing] : []}
                    onChange={(items) => setLine(i, { postProcessing: items[0] ?? '' })}
                  />
                )}
              </div>
            ) : null}

            {/*
              派工單專屬的生產指示(REQ-20260818002)。紙本上其餘的格子對應既有欄位:
              精樣名稱 = 品名、印刷用紙 = 規格、紙張尺寸 = 尺寸 / 開數、印刷數量 = 數量,
              所以這裡只補紙本有、系統原本沒有的那幾格。
            */}
            {isWorkOrder ? (
              <div className="grid gap-2 border-t pt-2" data-testid={`po-line-work-order-${i}`}>
                <div className="grid gap-2 md:grid-cols-5">
                  <Field
                    label="印工張數"
                    value={line.impressionQty}
                    onChange={(v) => setLine(i, { impressionQty: v })}
                  />
                  <Field
                    label="成品尺寸"
                    value={line.finishedSize}
                    onChange={(v) => setLine(i, { finishedSize: v })}
                  />
                  <Field
                    label="成品數量"
                    value={line.finishedQty}
                    onChange={(v) => setLine(i, { finishedQty: v })}
                  />
                  <label className="grid gap-0.5 text-xs text-slate-600">
                    是否校色
                    <span className="flex items-center gap-1 py-1.5">
                      <input
                        type="checkbox"
                        checked={line.colorProofRequired}
                        onChange={(e) => setLine(i, { colorProofRequired: e.target.checked })}
                      />
                      <span className="text-sm">需要校色</span>
                    </span>
                  </label>
                  <Field
                    label="特殊項目"
                    value={line.specialItems}
                    onChange={(v) => setLine(i, { specialItems: v })}
                  />
                </div>
              </div>
            ) : null}

            {lines.length > 1 ? (
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => setLines((prev) => prev.filter((_, idx) => idx !== i))}
                  className="text-red-600 hover:underline text-xs"
                >
                  刪除此列
                </button>
              </div>
            ) : null}
          </div>
        ))}
        <button
          type="button"
          onClick={() => setLines((prev) => [...prev, { ...EMPTY_LINE }])}
          className="justify-self-start text-blue-600 hover:underline text-sm"
        >
          + 新增品項
        </button>
      </fieldset>
      ) : null}

      <div className="grid gap-1">
        <label className="text-sm text-slate-600" htmlFor="po-notes">
          備註
        </label>
        <textarea
          id="po-notes"
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="border rounded-md px-3 py-2 text-sm"
        />
      </div>

      {/*
        金額與結帳(REQ-20260907002 附圖第四張卡)。**只有運費類**。

        三個數字沿用既有的 `calcPurchaseTotals()`,稅別沿用**既有的單頭稅別下拉**
        (不新增第二格 —— 那一格已經是這張表單唯一決定稅別的地方)。
        下面「依付款方式拆分」的三格是同一支函式各算一次子集合,見 `freightSplit`。

        ⚠️ **只做顯示,不自動沖帳**:現付那一格標的是「這筆錢已經付掉了」,
        但系統沒有為它產生任何應付沖帳 —— 那會動到過帳流程(要在 confirm 的
        transaction 裡開一筆 ap_settlement,比照 REQ-20260829003 的做法)。
        本次刻意不做,理由與待確認事項寫在 REQ-20260907002。
        也**沒有動** `calcOutstanding()` 或應付未結的定義。
      */}
      {isFreight ? (
        <fieldset className="grid gap-3 border rounded-md p-3" data-testid="po-freight-totals">
          <legend className="text-sm font-medium text-slate-700">金額與結帳</legend>
          <div className="grid gap-3 md:grid-cols-3 items-end">
            <AutoField label="未稅小計" value={withThousands(totals.subtotal)} />
            <Select
              id="po-tax-category"
              label="稅別"
              value={taxMode}
              onChange={(v) => setTaxMode(v as TaxSelection)}
              options={
                legacyZeroRated
                  ? { ...PURCHASE_TAX_MODES, ZERO_RATED: PURCHASE_TAX_CATEGORIES.ZERO_RATED }
                  : PURCHASE_TAX_MODES
              }
            />
            <AutoField label="含稅總計" value={withThousands(totals.total)} />
          </div>

          <div className="grid gap-2 border-t pt-3">
            <p className="text-[11px] text-slate-500">依付款方式拆分</p>
            <div className="grid gap-2 md:grid-cols-3">
              <div className="rounded-md border p-2" data-testid="po-freight-split-monthly">
                <p className="text-[11px] text-slate-500">列入應付帳款</p>
                <p className="font-mono text-lg">{withThousands(freightSplit.MONTHLY.total)}</p>
                <p className="text-[11px] text-slate-400">
                  {supplier?.paymentDays != null ? `月結 ${supplier.paymentDays} 天` : '依廠商付款條件'}
                </p>
              </div>
              <div
                className="rounded-md border border-green-200 bg-green-50 p-2"
                data-testid="po-freight-split-cash"
              >
                <p className="text-[11px] text-green-700">
                  已付訖{cashSource ? ` · 沖${cashSource}` : ''}
                </p>
                <p className="font-mono text-lg text-green-700">
                  {withThousands(freightSplit.CASH.total)}
                </p>
                <p className="text-[11px] text-green-700">不產生應付</p>
              </div>
              <div
                className="rounded-md border border-dashed p-2"
                data-testid="po-freight-split-collect"
              >
                <p className="text-[11px] text-slate-500">到付 · 僅記錄</p>
                <p className="font-mono text-lg text-slate-500">
                  {withThousands(freightSplit.COLLECT.total)}
                </p>
                <p className="text-[11px] text-slate-400">不入帳</p>
              </div>
            </div>
          </div>
        </fieldset>
      ) : null}

      {/*
        成本歸屬(REQ-20260907002 附圖第五張卡)。**只有運費類**。
        ⚠️ 後兩種**只做記錄與顯示,不做金額分攤計算** —— 分攤比例規則
        (按金額?按件數?按重量?)使用者沒給,猜一個等於偽造成本。
        欄位先落地讓資料收得起來,算法待使用者確認。
      */}
      {isFreight ? (
        <fieldset className="grid gap-2 border rounded-md p-3" data-testid="po-cost-allocation">
          <div className="flex items-baseline justify-between gap-2">
            <legend className="text-sm font-medium text-slate-700">成本歸屬 *</legend>
            <span className="text-[11px] text-slate-400">費用類進貨不進庫存,必須指定去向才能結案</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {(
              Object.keys(PURCHASE_COST_ALLOCATION_MODES) as PurchaseCostAllocationMode[]
            ).map((mode) => (
              <button
                key={mode}
                type="button"
                data-testid={`po-cost-allocation-${mode}`}
                aria-pressed={costAllocationMode === mode}
                onClick={() => setCostAllocationMode(mode)}
                className={`rounded border px-3 py-1.5 text-sm ${
                  costAllocationMode === mode
                    ? 'border-blue-600 bg-blue-600 text-white'
                    : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                {PURCHASE_COST_ALLOCATION_MODES[mode]}
              </button>
            ))}
          </div>
          {costAllocationMode === 'SPREAD_WORK_ORDERS' || costAllocationMode === 'OPERATING_EXPENSE' ? (
            <p className="text-[11px] text-slate-400">
              目前只記錄這個選擇,系統不會自動把金額分攤到各張工單(分攤規則待確認)。
            </p>
          ) : null}
        </fieldset>
      ) : null}

      {/*
        單頭稅別與合計並排(REQ-20260824001 起的版面,REQ-20260901010 換成單頭稅別):
        改成免稅時稅額就是 0 —— 下拉擺在看得到結果的旁邊,改完立刻對得起來。
        沒有 placeholder:單頭沒有「沿用」的對象,一定是三選一。
        `border-t pt-3` 從 `<dl>` 移到這層外框,不然兩者各畫一條就變成兩條分隔線。
        選項自 REQ-20260902011 起與廠商主檔的「稅別」一致(含稅 / 稅外加 / 免稅),
        一顆下拉同時決定 `taxCategory` 與 `unitPriceTaxInclusive`;建單時選廠商會把
        這格預設成該廠商的稅別(見 `handleSupplierChange`),之後不會再被系統蓋回去。
        零稅率只在編輯舊單時多出來(`legacyZeroRated`)—— 少了這顆選項,PATCH 會把
        舊單的零稅率靜默改成應稅。
      */}
      {!isFreight ? (
      <div className="grid gap-3 border-t pt-3 md:grid-cols-2 md:items-end">
        <Select
          id="po-tax-category"
          label="稅別"
          value={taxMode}
          onChange={(v) => setTaxMode(v as TaxSelection)}
          options={
            legacyZeroRated
              ? { ...PURCHASE_TAX_MODES, ZERO_RATED: PURCHASE_TAX_CATEGORIES.ZERO_RATED }
              : PURCHASE_TAX_MODES
          }
        />

        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm md:w-80 md:justify-self-end">
          <dt className="text-slate-500">未稅合計</dt>
          <dd className="text-right font-mono">{formatMoney(totals.subtotal)}</dd>
          <dt className="text-slate-500">其中應稅</dt>
          <dd className="text-right font-mono">{formatMoney(totals.taxableSubtotal)}</dd>
          <dt className="text-slate-500">稅額(整數元)</dt>
          <dd className="text-right font-mono">{formatMoney(totals.tax)}</dd>
          <dt className="font-medium text-slate-700">含稅總計</dt>
          <dd className="text-right font-mono font-medium">{formatMoney(totals.total)}</dd>
        </dl>
      </div>
      ) : null}

      {/*
        單據附件(REQ-20260907002)。**共用元件、無條件渲染** ——
        四種進貨類別、四種付款方式都看得到(需求原文:「任何進貨單都要能貼單據」)。
        建單模式下單還不存在(presign 需要 id):選到的檔案先暫存在這張表單,
        建單成功拿到 id 之後才補傳(REQ-20261012001,推翻了 REQ-20260907002
        「上傳鈕停用、不為此改建單流程」那一條,見 `submitOrder()`)。
        建單 / 補傳進行中卡片要停用:補傳的是按下送出那一刻的清單,這時再選的檔案
        不會被傳、畫面一換就無聲消失。
        建立成功後的附件步驟會再出現同一顆卡片,可以補檔案、改類型。
        已送出確認 / 已過帳的單:看得到、預覽得到,不能增刪(R-12 + R-08)。
      */}
      <DocumentAttachmentCard
        docType="PURCHASE_ORDER"
        docId={editOrder?.id}
        disabledReason={
          editOrder != null && editOrder.status !== 'DRAFT'
            ? '已送出確認的單據附件唯讀,要修改請先退回草稿'
            : !isEdit && isPending
              ? '建立中,請稍候'
              : undefined
        }
        {...(isEdit
          ? {}
          : { pendingFiles: pendingDocFiles, onPendingFilesChange: setPendingDocFiles })}
      />

      {/*
        工單附件(REQ-20260917001)。**同一顆共用元件、同一張進貨單**,只是換一個
        `docType` 把檔案分成兩個櫃子:上面那張放廠商給的單據,這張放我們自己的
        工單 / 施工圖。「拍照 | 選檔案」兩顆鈕是元件本來就有的,不另寫一份上傳 UI。
        渲染條件、停用條件、凍結規則、建單模式的暫存都與上面那張逐字相同(R-12 + R-08)。
      */}
      <DocumentAttachmentCard
        docType="PURCHASE_ORDER_WORK_ORDER"
        docId={editOrder?.id}
        title="工單附件"
        subtitle="工單、施工圖、排版稿、客戶提供的檔案"
        testIdPrefix="work-order-attachment"
        disabledReason={
          editOrder != null && editOrder.status !== 'DRAFT'
            ? '已送出確認的單據附件唯讀,要修改請先退回草稿'
            : !isEdit && isPending
              ? '建立中,請稍候'
              : undefined
        }
        {...(isEdit
          ? {}
          : {
              pendingFiles: pendingWorkOrderFiles,
              onPendingFilesChange: setPendingWorkOrderFiles,
            })}
      />

      {taxInclusiveOutOfTolerance ? (
        <div role="status" className="text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2 text-sm">
          反推後的含稅總計與輸入的含稅金額差 {totals.taxInclusiveDiff} 元(超過 ±
          {PURCHASE_TAX_INCLUSIVE_TOLERANCE} 元)。稅別選的是「含稅」,請確認輸入的單價
          確實含稅 —— 免稅 / 零稅率品項的單價不會被反推。
        </div>
      ) : null}

      {formError ? (
        <div role="alert" className="text-red-600 text-sm">
          {formError}
        </div>
      ) : null}

      {/*
        運費類的底部工具列(REQ-20260907002 附圖最下面那一條)。
        左邊列出待補項目、右邊兩顆鈕:
         - **「儲存草稿」永遠可以按** —— 草稿本來就是拿來邊做邊存的
         - **「確認入帳」在有待補項目時停用**,補齊了才按得下去

        ⚠️「確認入帳」= 先存檔、再打既有的 `POST /:id/submit`(送出確認)。
        它**沒有跳過 R-08 的狀態機**:最後那一步「確認」仍然只有 ADMIN 能在
        清單頁按(REQ-20260813001 §二)。按鈕文字照附圖,底下那行小字說明
        它實際走到哪一步 —— 自己在表單裡再開一條 confirm 路徑等於把清單頁的
        守門複製一份。
      */}
      {isFreight ? (
        <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3">
          <span className="text-[11px] text-slate-400">
            {freightPending.length > 0 ? `待補:${freightPending.join('、')}` : '待補項目已補齊'}
          </span>
          <div className="flex items-center gap-2">
            {/* 未儲存的小標記(REQ-20260917002)。運費版面的「儲存草稿 / 確認入帳」
                **顏色不動** —— 那兩顆不是「儲存修改」,而且右邊已經有一顆藍色的
                「確認入帳」,再多一顆藍的會分不出主次。提醒仍然看得到 */}
            {dirty ? (
              <span className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-0.5 shrink-0">
                未儲存
              </span>
            ) : null}
            <button
              type="button"
              onClick={handleCancel}
              className="px-3 py-1.5 rounded border text-slate-700 hover:bg-slate-50"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={isPending}
              data-testid="po-freight-save-draft"
              className="px-3 py-1.5 rounded border text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {isPending ? '儲存中...' : '儲存草稿'}
            </button>
            <button
              type="button"
              disabled={isPending || freightPending.length > 0}
              onClick={() => onSubmit(true)}
              data-testid="po-freight-confirm"
              title="儲存後送出確認,最後一步由管理者在清單頁確認才會計入應付"
              className="bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {isPending ? '處理中...' : '確認入帳'}
            </button>
          </div>
        </div>
      ) : (
      <div className="flex gap-2 justify-end items-center">
        {/* 未儲存的小標記(REQ-20260917002),樣式沿用廠商操作台標題旁那一顆 */}
        {dirty ? (
          <span className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-0.5 shrink-0">
            未儲存
          </span>
        ) : null}
        <button
          type="button"
          onClick={handleCancel}
          className="px-3 py-1.5 rounded border text-slate-700 hover:bg-slate-50"
        >
          取消
        </button>
        {/* 編輯模式的「儲存修改」依 dirty 變色(REQ-20260917002)——
            沒改過時長得和左邊的「取消」一樣,一動欄位就變藍底白字。
            灰框那組就是取消鍵的 class 加 `disabled:opacity-50`;**padding 保持
            `px-3 py-1.5` 不動**,不然切換 dirty 時整排按鈕會抖一下
            (REQ-20260819011 §二取捨 1)。
            建單模式(「建立草稿」)一律藍色,與改動前逐字相同(REQ-20260825009 的決定)。 */}
        <button
          type="submit"
          disabled={isPending}
          className={
            isEdit && !dirty
              ? 'px-3 py-1.5 rounded border text-slate-700 hover:bg-slate-50 disabled:opacity-50'
              : 'bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50'
          }
        >
          {isEdit
            ? isPending
              ? '儲存中...'
              : '儲存修改'
            : isPending
              ? '建立中...'
              : '建立草稿'}
        </button>
      </div>
      )}

      {/* 有品項沒填單位 / 單價時的提醒(REQ-20260911001 需求第 3 點、
          REQ-20260917001 需求第 1 點)。**是提醒不是驗證** —— 按「仍要儲存」就照
          原本的流程存下去,`unitName` 在 schema 上仍然是選填,單價則補成 0。
          用 `ConfirmDialog` 而不是 `window.confirm`(REQ-20260903009 /
          REQ-20260907004 的既有慣例)。
          `tone` 用預設的 primary:這不是危險動作,紅鈕會讓它看起來像刪除。
          兩種未填**分行列出**,哪一格沒填一眼看得出來。 */}
      <ConfirmDialog
        open={unitConfirm != null}
        title="有品項尚未填寫"
        message={
          <span className="grid gap-1">
            {linesMissingUnit.length > 0 ? (
              <span>第 {linesMissingUnit.join('、')} 列未填單位</span>
            ) : null}
            {linesMissingPrice.length > 0 ? (
              <span>第 {linesMissingPrice.join('、')} 列未填單價</span>
            ) : null}
            <span>仍要儲存嗎?</span>
          </span>
        }
        confirmLabel="仍要儲存"
        cancelLabel="回去補填"
        onConfirm={() => submitOrder(unitConfirm?.thenSubmit ?? false)}
        onClose={() => setUnitConfirm(null)}
      />

      {/* 有未儲存變更時按「取消」的攔截(REQ-20260917002)。文案逐字沿用客戶 /
          廠商操作台(REQ-20260916004),同一件事不寫成兩種說法 */}
      <ConfirmDialog
        open={cancelConfirm}
        title="放棄未儲存的變更?"
        message="剛剛輸入但還沒儲存的內容會全部消失,已經存進系統的資料不受影響。"
        confirmLabel="放棄變更"
        cancelLabel="繼續編輯"
        tone="danger"
        onConfirm={onCancel}
        onClose={() => setCancelConfirm(false)}
      />
    </form>
  );
}

/**
 * 運費明細每一列的「歸屬工單」挑選框(REQ-20260908001)。
 *
 * 選項來源是**該列自己的送達客戶**,不是單頭的代管客戶 —— 運費版面根本不渲染
 * 代管客戶那一格(送給誰是逐列的事),沿用單頭那份清單的結果是永遠「已選 0/0」,
 * 一張工單都挑不到。這趟貨是為了哪張出貨單而送的,答案就在該列的送達客戶身上。
 *
 * 抽成子元件是因為 **hooks 不能在 `lines.map()` 迴圈裡呼叫** —— 每一列的客戶不同,
 * 就得各自查一次。`slice(0, 10)` 與 `display` 的組法、`orderNo ↔ display` 的雙向反查
 * 都與單頭那份逐字相同(REQ-20260819002 / REQ-20260819003 / REQ-20260905001):
 * 存進 state 與送出的值仍然**只有純單號**。
 *
 * 單頭的 `workOrderNoOptions` 一行都沒動 —— 非運費類的品項那一格仍然依代管客戶。
 */
function FreightWorkOrderPicker({
  customerId,
  testId,
  value,
  onChange,
}: {
  customerId: number | undefined;
  testId: string;
  value: string[];
  onChange: (nos: string[]) => void;
}): JSX.Element {
  const { data: deliveryOrders } = useSalesOrders({
    customerId,
    enabled: customerId != null,
  });
  const options = (deliveryOrders?.items ?? []).slice(0, 10).map((o) => ({
    orderNo: o.orderNo,
    display: o.firstProductName
      ? `${o.orderNo} - ${o.firstProductName}${o.lineCount > 1 ? ` 等${o.lineCount}項` : ''}`
      : o.orderNo,
  }));

  return (
    <>
      <MultiSelectField
        label="歸屬工單"
        testId={testId}
        compact
        options={options.map((o) => o.display)}
        selected={value.map((no) => options.find((o) => o.orderNo === no)?.display ?? no)}
        onChange={(items) =>
          onChange(items.map((v) => options.find((o) => o.display === v)?.orderNo ?? v))
        }
      />
      {/* 還沒選送達客戶就查不出清單,但**不停用、不擋輸入** —— 這一格是建議不是值域
          (REQ-20260819002 §二.1),手打的單號照樣進得了已選清單 */}
      {customerId == null ? (
        <span className="text-[11px] text-slate-400">先選送達客戶才有工單可挑</span>
      ) : null}
    </>
  );
}

/**
 * 運費分攤面板(REQ-20260908002 圖二)——「一筆運費對到多張工單,這筆錢怎麼分」。
 *
 * **就地展開,不跳畫面**(需求原文)。三種方式:依品項金額比例(預設)/ 平均分攤 /
 * 手動指定;金額四捨五入的尾差補到第一列。
 *
 * ⚠️ 算式本身在 `packages/shared` 的 `allocateFreight()` —— **前後端共用同一支**,
 * 這裡只負責把權重(各工單的單據金額)餵進去、把結果畫出來。
 * 在元件裡自己再算一次就是「同一筆成本有兩個答案」。
 *
 * 權重來源與 `FreightWorkOrderPicker` 同一份查詢(依該列的送達客戶查訂單),
 * react-query 會共用快取,不會多打一次。查不到金額時 `allocateFreight()` 會
 * 自動退回平均分攤(見它的註解),不會除以 0。
 */
function FreightAllocationPanel({
  testId,
  amount,
  customerId,
  workOrderNos,
  method,
  amounts,
  onChange,
}: {
  testId: string;
  /** 這一列的金額(未稅) */
  amount: string;
  customerId: number | undefined;
  workOrderNos: string[];
  method: '' | FreightAllocationMethod;
  amounts: Record<string, string>;
  onChange: (patch: Partial<LineDraft>) => void;
}): JSX.Element {
  const { data: deliveryOrders } = useSalesOrders({
    customerId,
    enabled: customerId != null,
  });
  const totalByOrderNo = new Map(
    (deliveryOrders?.items ?? []).map((o) => [o.orderNo, o.total] as const),
  );
  // '' 視為預設的「依品項金額比例」—— 面板一展開就看得到一組算好的數字
  const effective: FreightAllocationMethod = method || 'BY_AMOUNT';
  const result = allocateFreight(
    amount,
    effective,
    workOrderNos.map((no) => ({
      workOrderNo: no,
      baseAmount: totalByOrderNo.get(no) ?? null,
      manualAmount: amounts[no] ?? null,
    })),
  );

  /** 換分攤方式時**立刻把算出來的金額寫回 draft** —— 面板上看到的就是會送出的值 */
  const pick = (next: FreightAllocationMethod): void => {
    const computed = allocateFreight(
      amount,
      next,
      workOrderNos.map((no) => ({
        workOrderNo: no,
        baseAmount: totalByOrderNo.get(no) ?? null,
        manualAmount: amounts[no] ?? null,
      })),
    );
    onChange({
      allocationMethod: next,
      // 手動指定時保留使用者已經打進去的值,其餘兩種用算出來的
      workOrderAmounts:
        next === 'MANUAL'
          ? amounts
          : Object.fromEntries(computed.rows.map((r) => [r.workOrderNo, r.amount])),
    });
  };

  return (
    <div
      className="rounded border border-amber-200 bg-amber-50 px-3 py-2 grid gap-2"
      data-testid={testId}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] text-amber-800">分攤方式</span>
        {(Object.keys(FREIGHT_ALLOCATION_METHODS) as FreightAllocationMethod[]).map((m) => (
          <button
            key={m}
            type="button"
            data-testid={`${testId}-method-${m}`}
            aria-pressed={effective === m}
            onClick={() => pick(m)}
            className={`rounded border px-1.5 py-0.5 text-[11px] ${
              effective === m
                ? 'border-blue-600 bg-blue-50 text-blue-700'
                : 'border-amber-300 text-slate-700 hover:bg-white'
            }`}
          >
            {FREIGHT_ALLOCATION_METHODS[m]}
          </button>
        ))}
      </div>

      <table className="w-full bg-white shadow-sm rounded-md">
        <thead>
          <tr className="border-b text-left text-[11px] text-slate-600">
            <th className="px-2 py-1">工單</th>
            <th className="px-2 py-1 text-right">佔比</th>
            <th className="px-2 py-1 text-right">金額</th>
          </tr>
        </thead>
        <tbody>
          {result.rows.map((r) => (
            <tr key={r.workOrderNo} className="border-b last:border-0 text-[11px]">
              <td className="px-2 py-1 font-mono">{r.workOrderNo}</td>
              <td className="px-2 py-1 text-right font-mono text-slate-500">{r.percent}%</td>
              <td className="px-2 py-1 text-right">
                {effective === 'MANUAL' ? (
                  <input
                    type="text"
                    inputMode="decimal"
                    aria-label={`${r.workOrderNo} 分攤金額`}
                    data-testid={`${testId}-amount-${r.workOrderNo}`}
                    value={amounts[r.workOrderNo] ?? ''}
                    onChange={(e) =>
                      onChange({
                        allocationMethod: 'MANUAL',
                        workOrderAmounts: { ...amounts, [r.workOrderNo]: e.target.value },
                      })
                    }
                    className="w-24 border rounded px-1 py-0.5 text-right font-mono"
                  />
                ) : (
                  <span className="font-mono">{r.amount}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {result.error ? (
        <span className="text-[11px] text-red-600" data-testid={`${testId}-error`}>
          {result.error}
        </span>
      ) : (
        <span className="text-[11px] text-slate-500" data-testid={`${testId}-total`}>
          合計 {result.total} —— 四捨五入的尾差補在第一列
        </span>
      )}
    </div>
  );
}

/**
 * 建單後的附件步驟。附件只有草稿能增刪(R-12),送出確認之後就唯讀了。
 *
 * `failedUploads`(REQ-20261012001):建單前先選好、建單後補傳失敗的檔名。
 * 「確認入帳」遇到這種情形也會停在這裡、**不送出確認** —— 送出後就補不進來了,
 * 補完要不要送出由使用者自己到清單決定。
 */
function PurchaseOrderAttachmentStep({
  order,
  failedUploads,
  onDone,
}: {
  order: { id: number; orderNo: string };
  failedUploads: string[];
  onDone: () => void;
}): JSX.Element {
  return (
    <div className="grid gap-3 bg-white shadow-sm rounded-md p-4">
      {/* 建立成功後同一個位置改印後端回的**正式**單號,不再是預覽值 */}
      <OrderNoBlock orderNo={order.orderNo} isPreview={false} />

      <div role="status" className="text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2 text-sm">
        已建立進貨單 <span className="font-mono">{order.orderNo}</span>(草稿)。
        可在這裡上傳發票 / 送貨單掃描檔 —— 送出確認後附件就唯讀了。
      </div>

      {failedUploads.length > 0 ? (
        <div
          role="alert"
          data-testid="po-pending-upload-failed"
          className="text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2 text-sm"
        >
          單已建立,以下檔案未上傳成功:{failedUploads.join('、')},請在下方附件區補傳。
          這張單目前仍是草稿、尚未送出確認,補齊後再到清單按「送出確認」。
        </div>
      ) : null}

      <DocumentAttachmentCard docType="PURCHASE_ORDER" docId={order.id} />

      {/* 工單附件(REQ-20260917001)。建單後這一步也要有,否則新開的單只能貼
          廠商單據、工單得等到重新進編輯畫面才貼得上去 */}
      <DocumentAttachmentCard
        docType="PURCHASE_ORDER_WORK_ORDER"
        docId={order.id}
        title="工單附件"
        subtitle="工單、施工圖、排版稿、客戶提供的檔案"
        testIdPrefix="work-order-attachment"
      />

      <div className="flex justify-end">
        <button
          type="button"
          onClick={onDone}
          className="bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700"
        >
          完成
        </button>
      </div>
    </div>
  );
}

/**
 * 單號區塊(REQ-20260813004)。版面與出貨單表單的 `so-order-no` 逐格對應 ——
 * 需求要求兩張表單「架構規則都一樣」。
 *
 * 建單時顯示的是**預覽值**:序號由後端在寫入當下決定、撞號還會重算重試
 * (CLAUDE.md §6.3),所以旁邊固定掛提示,不讓使用者以為這是保證值。
 * 建立成功後(附件步驟)換成後端回的正式號碼,提示跟著拿掉。
 * 預覽還沒回來時先顯示「—」,不要留一個看起來壞掉的空白。
 */
function OrderNoBlock({
  orderNo,
  isPreview,
  bare = false,
}: {
  orderNo: string | undefined;
  isPreview: boolean;
  /**
   * 收掉底線與下邊距(REQ-20260907002)。**預設 false,不傳時 DOM 與 class 逐字不變**。
   * 運費類把它排進頂部提醒列裡,那條列自己已經有邊框了。
   */
  bare?: boolean;
}): JSX.Element {
  return (
    <div
      className={`flex items-baseline gap-2 text-sm ${
        bare ? '' : 'border-b border-slate-200 pb-2'
      }`}
      data-testid="po-order-no"
    >
      <span className="text-slate-500">單號</span>
      <strong className="font-mono text-slate-900">{orderNo ?? '—'}</strong>
      {isPreview ? <span className="text-xs text-slate-400">預覽,實際以儲存後為準</span> : null}
    </div>
  );
}

/**
 * `className` 只給呼叫端指定這一格在格線上佔幾欄(`md:col-span-*`,REQ-20260903002)——
 * 品項的三個區塊共用同一條 12 欄格線,寬度必須逐格指定才對得齊。
 * 不要拿它改字級 / 邊框:那是這顆元件自己的事,散到呼叫端就會慢慢分岔。
 */
function Field({
  label,
  value,
  onChange,
  placeholder,
  className,
  alignRight,
  inputRef,
  onKeyDown,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
  /**
   * 數字欄位靠右對齊(REQ-20260905006)。**只加 `text-right`** ——
   * 不會順便插千分位:受控 input 在使用者打字途中改寫值會吃掉按鍵、游標跳位。
   * 千分位只用在唯讀的 `AutoField`。
   */
  alignRight?: boolean;
  /** 品項那一排的鍵盤跳格要拿得到這一格(REQ-20260911001)。不傳時 DOM 完全不變 */
  inputRef?: React.Ref<HTMLInputElement> | undefined;
  onKeyDown?: ((e: React.KeyboardEvent<HTMLInputElement>) => void) | undefined;
}): JSX.Element {
  return (
    <label className={`grid gap-0.5 text-xs text-slate-600 ${className ?? ''}`}>
      {label}
      <input
        type="text"
        value={value}
        ref={inputRef}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className={`w-full border rounded-md px-2 py-1.5 text-sm ${alignRight ? 'text-right' : ''}`}
      />
    </label>
  );
}

/**
 * 單位沒填時掛在該格下面的淡色提示(REQ-20260911001 需求第 3 點)。
 *
 * **不是驗證失敗的紅字** —— `unitName` 在 zod 上仍然是選填,正式環境很多舊單就是
 * 沒填單位,把它畫成 error 會讓那些單看起來像壞掉的。這只是「這格空著喔」。
 *
 * ⚠️ 一律掛在 `<label>` 的**外面**:`Field` 是一個包住 input 的 `<label>`,
 * 提示文字放進去會被算進可及名稱,`getByLabelText('單位')` 就變成要找
 * 「單位 單位還沒填」(同一張表單為了 `<button>` 踩過的坑,見 `NumberUnitField`)。
 */
function UnitMissingHint({ index }: { index: number }): JSX.Element {
  return (
    <p className="text-[11px] text-slate-400" data-testid={`po-line-unit-hint-${index}`}>
      單位還沒填
    </p>
  );
}

/**
 * 單價沒填時掛在該格下面的淡色提示(REQ-20260917001 需求第 1 點)。
 *
 * 樣式與 `UnitMissingHint` 逐字相同、**刻意不是紅色**:商品還沒進貨、金額未定是
 * 正常的打單過程(同 REQ-20260911001 §2.4 對舊單的態度),畫成驗證失敗會讓
 * 一整批待進貨的草稿看起來像壞掉的。
 *
 * ⚠️ 顯示條件是「空字串**或** 0」,與送出前那個提醒視窗(只看空字串)**刻意不同** ——
 * 送出時空白會被存成 `'0'`,重開草稿時看到的是 0 而不是空白,只看空字串的話提醒
 * 一重開就消失了。判定不同的另一半理由寫在 `linesMissingPrice` 上面。
 *
 * 與 `UnitMissingHint` 相同,一律掛在 `Field` 那個 `<label>` 的**外面**:
 * 放進去會被算進可及名稱,`getByLabelText('單價')` 就對不上了。
 */
function PriceMissingHint({ index }: { index: number }): JSX.Element {
  return (
    <p className="text-[11px] text-slate-400" data-testid={`po-line-price-hint-${index}`}>
      單價尚未填寫,可先存成草稿,進貨到料時再補
    </p>
  );
}

/** 單價那一格要不要顯示提示。空白或 0 都算「還沒 KEY」,理由見 `PriceMissingHint` */
function isPriceMissing(unitPrice: string): boolean {
  const v = unitPrice.trim();
  return v === '' || Number(v) === 0;
}

/**
 * 標籤右邊帶一個灰色小標的欄位(REQ-20260905006:「自動」/「自動計算」)。
 *
 * 用 `div` + `span` 而不是包住 children 的 `<label>`:小標跟著進 label 之後
 * 標籤文字會變成「總價 自動計算」,`getByLabelText('總價')` 就對不上了 ——
 * 標籤關聯改由 children 自己的 `aria-label` 承接(同 `SupplierForm` 的 `GroupField`)。
 */
function TaggedField({
  label,
  tag,
  className,
  children,
}: {
  label: string;
  tag: string;
  /**
   * 同 `Field`:只給 `md:col-span-*`。
   * 明寫 `| undefined` 是因為 `exactOptionalPropertyTypes` —— `AutoField` 轉傳
   * 自己那個 optional 的 className 進來,少了它 tsc 會紅。
   */
  className?: string | undefined;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className={`grid gap-0.5 text-xs text-slate-600 ${className ?? ''}`}>
      <span className="flex items-baseline gap-1">
        {label}
        <span className="text-[10px] text-slate-400">{tag}</span>
      </span>
      {children}
    </div>
  );
}

/**
 * 系統自己算出來的唯讀欄位(REQ-20260905006):灰底 + 標籤旁一個「自動」小標。
 * 值走 `<output>`(不是 disabled input)—— 沿用改動前「庫存單位數量」既有的做法,
 * 它本來就不進 payload。
 */
function AutoField({
  label,
  value,
  className,
}: {
  label: string;
  value: string;
  className?: string;
}): JSX.Element {
  return (
    <TaggedField label={label} tag="自動" className={className}>
      <output
        aria-label={label}
        className="w-full border rounded-md bg-slate-50 px-2 py-1.5 text-right text-sm font-mono"
      >
        {value}
      </output>
    </TaggedField>
  );
}

/**
 * 數字輸入框 + 右邊貼著一顆單位挑選鈕的複合欄位(REQ-20260905006,「數量」與「厚度」)。
 * 收合狀態下整格一行高,與同一列其他格上下緣齊平。
 *
 * ⚠️ 兩個控制項共用一個標籤,所以用 `div` + `span` + 各自的 `aria-label`,
 * **不要**把 `<button>` 包進 `<label>` —— button 是 labelable element,包進去會讓
 * `getByLabelText` 一次抓到兩個元素(`SupplierForm` 的 `GroupField` 同一個理由)。
 *
 * 單位鈕點開的是既有的 `MultiSelectField`(精簡版 `compact`,REQ-20260905002):
 * 清單外的單位照樣打得進去、舊值原樣顯示成 chip。
 * ⚠️ 這一格**不能**換成封閉的 `<select>`(REQ-20260902003 §2.2 起已三次否決):
 * 正式資料有清單外的舊值,`<select>` 對不到 option 會顯示空白、一存檔就靜默清掉。
 */
function NumberUnitField({
  label,
  value,
  onChange,
  unitLabel,
  unitTestId,
  unitOptions,
  unitValue,
  onUnitChange,
  className,
  inputRef,
  onKeyDown,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  unitLabel: string;
  unitTestId: string;
  unitOptions: string[];
  unitValue: string;
  onUnitChange: (v: string) => void;
  /** 同 `Field`:只給 `md:col-span-*` */
  className?: string;
  /** 數字框的跳格接線(REQ-20260911001)。單位鈕不在跳格鏈上 —— 見呼叫端的說明 */
  inputRef?: React.Ref<HTMLInputElement> | undefined;
  onKeyDown?: ((e: React.KeyboardEvent<HTMLInputElement>) => void) | undefined;
  /**
   * 整格最底下的淡色提示(REQ-20260911001 的「單位還沒填」)。不傳時不多出任何節點。
   * 掛在這裡而不是呼叫端:這一格的根節點是 `div`,提示進不了可及名稱
   * (數字框自己有 `aria-label`),不會影響 `getByLabelText`。
   */
  hint?: React.ReactNode;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <div className={`grid gap-0.5 text-xs text-slate-600 ${className ?? ''}`}>
      <span>{label}</span>
      <div className="flex">
        <input
          type="text"
          value={value}
          ref={inputRef}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          aria-label={label}
          // `min-w-0` 不可省:flex 子項的 input 有瀏覽器預設最小寬(實測約 172px),
          // 少了它會把單位鈕擠出格子(REQ-20260902004 踩過同一個坑)
          className="w-full min-w-0 rounded-l-md border px-2 py-1.5 text-right text-sm"
        />
        {/* aria-label 刻意不是 `unitLabel` 本身:挑選框展開時它的 legend 就叫那個名字,
            兩個元素同名會讓 `getByLabelText` 一次抓到兩個 */}
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          aria-expanded={open}
          aria-label={`選擇${unitLabel}`}
          title={unitValue || unitLabel}
          data-testid={`${unitTestId}-picker`}
          className={`flex max-w-[7rem] shrink-0 items-center gap-0.5 rounded-r-md border border-l-0 bg-slate-100 px-2 text-xs hover:bg-slate-200 ${
            unitValue ? 'text-slate-700' : 'text-slate-400'
          }`}
        >
          <span className="truncate">{unitValue || unitLabel}</span>
          <span aria-hidden>{open ? '▴' : '▾'}</span>
        </button>
      </div>
      {open ? (
        <MultiSelectField
          label={unitLabel}
          testId={unitTestId}
          multiple={false}
          compact
          defaultExpanded
          options={unitOptions}
          selected={unitValue ? [unitValue] : []}
          onChange={(items) => onUnitChange(items[0] ?? '')}
        />
      ) : null}
      {hint}
    </div>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
  placeholder,
  className,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<[string, string]>;
  placeholder?: string;
  /** 同 `Field`:只給 `md:col-span-*` */
  className?: string;
}): JSX.Element {
  return (
    <label className={`grid gap-0.5 text-xs text-slate-600 ${className ?? ''}`}>
      {label}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border rounded-md px-2 py-1.5 text-sm"
      >
        {placeholder ? <option value="">{placeholder}</option> : null}
        {options.map(([k, v]) => (
          <option key={k} value={k}>
            {v}
          </option>
        ))}
      </select>
    </label>
  );
}

/** 單頭欄位用的輸入框(比品項的 Field 大一號,與既有的 md:grid-cols-2 版面一致) */
function TextField({
  id,
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  hint,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  hint?: string;
}): JSX.Element {
  // `content-start`(REQ-20261029001):隔壁格有 hint 或廠商卡片時,這一格會被 grid 的
  // stretch 撐高;沒有它,多出來的高度會平均分給 label 列與 input 列 → 框變高、往下掉。
  // 加了之後一律以自然高度靠上,多的空間留在下面。`Select` 同理。
  return (
    <div className="grid gap-1 content-start">
      <label className="text-sm text-slate-600" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        placeholder={placeholder ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className="border rounded-md px-3 py-2 text-sm"
      />
      {hint ? <p className="text-[11px] text-slate-400">{hint}</p> : null}
    </div>
  );
}

function Select({
  id,
  label,
  value,
  onChange,
  options,
  placeholder,
  hint,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Record<string, string>;
  placeholder?: string;
  /** 欄位下方的說明小字,寫法與 `TextField` 的 `hint` 相同(REQ-20260905006) */
  hint?: string;
}): JSX.Element {
  return (
    <div className="grid gap-1 content-start">
      <label className="text-sm text-slate-600" htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border rounded-md px-3 py-2 text-sm"
      >
        {placeholder ? <option value="">{placeholder}</option> : null}
        {Object.entries(options).map(([k, v]) => (
          <option key={k} value={k}>
            {v}
          </option>
        ))}
      </select>
      {hint ? <p className="text-[11px] text-slate-400">{hint}</p> : null}
    </div>
  );
}

/**
 * 編輯模式的品項初始值(REQ-20260814001)。DB 回的是 DECIMAL 字串(`qty` = "12.000"),
 * 原樣放進輸入框即可 —— 使用者不改的話原封不動送回去,改了才重算。
 */
function toLineDraft(l: PurchaseOrderDetail['lines'][number]): LineDraft {
  return {
    productName: l.productName,
    productSpec: l.productSpec ?? '',
    // 舊品項一律是 null(migration 0045 沒有回填)—— 帶回空字串 = 未填
    productMaterial: l.productMaterial ?? '',
    unitName: l.unitName ?? '',
    qty: l.qty,
    unitPrice: l.unitPrice,
    // 總價是畫面上算出來的輔助欄位,不讀單據上的 `amount` —— 顯示的一律是
    // 「數量 × 單價」的結果,與旁邊兩格永遠一致(REQ-20260821005)
    amount: calcLineAmount({ qty: l.qty, unitPrice: l.unitPrice }),
    paperColor: l.paperColor ?? '',
    weightGsm: l.weightGsm != null ? String(l.weightGsm) : '',
    // 舊品項一律是 null(migration 0043 沒有回填)—— 帶回空字串 = 未指定,
    // 不改動它就照樣送回 null,不會被默默寫成 'gsm'
    weightUnit: l.weightUnit ?? '',
    sizeSpec: l.sizeSpec ?? '',
    grainDirection: (l.grainDirection as PurchaseGrainDirection | null) ?? '',
    conversionRate: l.conversionRate ?? '1',
    // null 在品項上是「沿用單頭」而不是「沒有稅別」,對應下拉選單的空字串選項
    taxCategory: (l.taxCategory as PurchaseTaxCategory | null) ?? '',
    // 舊單的單一案號由 migration 0046 搬進 join table,這裡讀到的一律是陣列;
    // `?? []` 只是防舊版 API(部署交界)回不出這一格(REQ-20260905003)
    workOrderNos: l.workOrderNos ?? [],
    costDestination: l.costDestination as PurchaseCostDestination,
    printColor: l.printColor ?? '',
    specialItems: l.specialItems ?? '',
    colorProofRequired: l.colorProofRequired,
    impressionQty: l.impressionQty != null ? String(l.impressionQty) : '',
    finishedSize: l.finishedSize ?? '',
    finishedQty: l.finishedQty != null ? String(l.finishedQty) : '',
    postProcessing: l.postProcessing ?? '',
    completedQty: l.completedQty ?? '',
    // ── 運輸費用單的明細欄位(REQ-20260907002)。舊品項一律 null(0047 沒有回填)
    id: l.id,
    shipDate: l.shipDate ?? '',
    trackingNo: l.trackingNo ?? '',
    // 單據只存了 id + 簡稱,湊一個最小的 `PartyInfo` 出來讓挑選框顯示得出名字。
    // 使用者按「重新選擇」時整顆會被主檔的真資料換掉(同 `supplierFromOrder()` 的作法)
    deliveryCustomer:
      l.deliveryCustomerId != null
        ? emptyParty(
            'customer',
            l.deliveryCustomerId,
            l.deliveryCustomerSimpleName ?? '',
            l.deliveryCustomerSimpleName ?? '',
            null,
          )
        : null,
    linePaymentType: (l.linePaymentType as PurchaseLinePaymentType | null) ?? '',
    // ── 運費歸單的來源與分攤(REQ-20260908002)。`?? null` 只是防舊版 API
    //    在部署交界回不出這幾格(慣例同 workOrderNos 的 `?? []`)
    freightLineId: l.freightLineId ?? null,
    freightSourceOrderId: l.freightSourceOrderId ?? null,
    freightSourceOrderNo: l.freightSourceOrderNo ?? null,
    allocationMethod: (l.allocationMethod as FreightAllocationMethod | null) ?? '',
    workOrderAmounts: Object.fromEntries(
      (l.workOrderAllocations ?? [])
        .filter((a) => a.amount != null)
        .map((a) => [a.workOrderNo, a.amount ?? '']),
    ),
  };
}

/**
 * 複製模式的品項初始值(REQ-20260911003)。在 `toLineDraft()` 的結果上**清掉幾格**,
 * 不另外複製一份 —— 品項欄位有 30 幾個,兩份一定會分岔。
 *
 * 清掉的是「指向上一次那個案子 / 那一列」的四類值:
 *
 * - `workOrderNos` / `workOrderAmounts`:工單 / 案號指的是上一次那批客戶訂單。
 *   照抄的話這次的採購成本會靜靜掛到舊工單上,而且**驗證不會擋**
 *   (只有不入庫直送才檢查非空,REQ-20260813001 R-07),是最難事後發現的錯。
 * - `id`:新單的品項還沒有 DB 列,留著舊 id 會讓明細的「憑證」附件掛到別張單的列上。
 * - `freightLineId` / `freightSourceOrderId` / `freightSourceOrderNo`:來源運費明細
 *   是唯一的(REQ-20260908002),兩張單掛同一筆就對不上帳。運費類的單在清單頁
 *   本來就不給複製,這裡清掉是第二層保險。
 *
 * ⚠️ 範本的 `stockInType` 是不入庫直送時,清掉工單號後送出會被後端 `assertDirectShip()`
 * 擋下 —— **這是刻意接受的結果**。那個訊息本來就明確指路(要填工單號),
 * 為了避開它而偷偷保留舊工單,才是真正會出事的那條路。
 */
function toCopyLineDraft(l: PurchaseOrderDetail['lines'][number]): LineDraft {
  return {
    ...toLineDraft(l),
    workOrderNos: [],
    workOrderAmounts: {},
    id: null,
    freightLineId: null,
    freightSourceOrderId: null,
    freightSourceOrderNo: null,
  };
}

/**
 * 編輯模式下 PartyPicker 只是唯讀顯示(廠商不能換),用單據上已有的欄位湊出 PartyInfo。
 * 單據沒存統編 / 電話快照,這裡只能留 null —— 掛載後 `usePartyById()` 會把電話等欄位
 * 補成主檔的真資料(REQ-20260902012),在那之前顯示成「—」;
 * 地址用單據上的收貨地址 —— 那才是這張單當初談定的地址,不是主檔的現值,
 * 所以補值時**刻意不讓主檔的地址蓋掉它**。
 */
function supplierFromOrder(order: PurchaseOrderDetail): PartyInfo {
  return emptyParty('supplier', order.supplierId, order.supplierSimpleName, order.supplierFullName, order.receivingAddress);
}

/**
 * 用紙廠商(REQ-20260818002)。與 `supplierFromOrder` 同一套 —— 單據存了 id 與名稱快照,
 * 湊出 `PartyInfo` 就夠顯示;要換廠商時 `PartyPicker` 自己會去查主檔。
 */
function paperSupplierFromOrder(order: PurchaseOrderDetail): PartyInfo | null {
  if (order.paperSupplierId == null) return null;
  return emptyParty(
    'supplier',
    order.paperSupplierId,
    order.paperSupplierSimpleName ?? '',
    order.paperSupplierFullName ?? '',
    null,
  );
}

/**
 * 代管客戶。這裡湊出來的只是**主檔回來之前的暫用值**(單據沒存編號 / 電話快照)——
 * 掛載後 `usePartyById()` 會把它換成真的主檔資料(REQ-20260819004)。
 */
function customerFromOrder(order: PurchaseOrderDetail): PartyInfo | null {
  if (order.customerId == null) return null;
  const name = order.customerSimpleName ?? '';
  return emptyParty('customer', order.customerId, name, name, null);
}

function emptyParty(
  type: 'customer' | 'supplier',
  id: number,
  simpleName: string,
  fullName: string,
  address: string | null,
): PartyInfo {
  return {
    id,
    type,
    code: null,
    fullName,
    simpleName,
    taxId: null,
    address,
    addressInvoice: null,
    contact: null,
    phone: null,
    phones: [],
    email: null,
    settlementCycle: null,
    settlementCycleLabel: null,
    settlementDay: null,
    paymentCert: null,
    paymentMethod: null,
  };
}

/**
 * 耗損提示(REQ-20260821006)。兩格都填了才算得出來,只填一邊就回 null(畫面改印說明)。
 *
 * **只顯示、不回寫**:耗損量一律是「數量 − 完成數量」,存成第三個欄位就等於
 * 讓同一件事有兩份真相(態度同 REQ-20260821005 §四 對總價的處理)。
 * 交回得比送出去的多也講清楚,不要印出「耗損 -40」這種看不懂的東西。
 * 相減後取到小數 4 位(對齊 `qty` 的 Decimal(14,4))再去掉尾零 ——
 * 不然 10.5 − 3.3 會顯示成 7.199999999999999。
 */
function describeLoss(qty: string, completedQty: string): string | null {
  if (!qty.trim() || !completedQty.trim()) return null;
  const diff = Number((toNumber(qty) - toNumber(completedQty)).toFixed(4));
  if (diff === 0) return '數量與完成數量相同,沒有耗損。';
  return diff > 0 ? `耗損 ${diff}(數量 − 完成數量)` : `溢收 ${-diff}(交回比委外的多)`;
}

function todayInTaipei(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Taipei',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}
